<template>
  <div class="forms-page">
    <FormsHomeSkeleton v-if="busy" />

    <div v-if="!$can('view', 'forms')" class="empty-state forms-feature-request">
      <div class="feature-request-container">
        <div class="feature-request-header">
          <i class="fa fa-file-text-o feature-icon"></i>
          <h3>TeamIO Forms Not Available</h3>
          <p class="feature-description">
            Forms feature allows you to create custom patient forms, collect digital signatures, 
            and streamline your patient intake process.
          </p>
        </div>
        
        <FeatureRequestButton
          class="btn btn-primary"
          feature-key="FORMS"
          feature-label="Digital Forms"
          source-page="Forms page"
        />
        <FeatureRequestNote feature="FORMS" class="mx-auto" />
      </div>
    </div>

    <div v-if="$can('view', 'forms')" v-show="!busy">
      <!-- The way between today's settings screens and the one Forms Settings editor (release gate 'formsSettings'). It decides by role itself:
           always there for a Super Admin (until the stage is retired), for an office user only once the area is at 'invite' / 'default'. -->
      <FollowupsUiToggle
        v-if="canSeeFormsSettings"
        class="px-3"
        :target="formsSettingsOn ? 'old' : 'new'"
        page-key="formsSettings"
        :note-text="formsSettingsNote"
        @toggle="setFormsSettingsLayout" />
      <!-- Modern Header matching Wiki style -->
      <div class="forms-header">
        <div class="header-left">
          <h1 class="page-title">Teamio Forms</h1>
        </div>
        <div class="header-actions">
          <b-button
            id="create-packet-btn"
            variant="success"
            class="create-form-btn mr-2"
            @click="showPacketModal()"
          >
            <i class="fa fa-files-o" /> Create a Packet
          </b-button>
          <b-tooltip target="create-packet-btn" placement="bottom" triggers="hover">
            By creating a packet, you can group multiple forms into one URL/Link to send to a patient
          </b-tooltip>
          <b-button
            v-if="aiFormsEnabled"
            id="create-ai-btn"
            variant="primary"
            class="create-form-btn mr-2"
            @click="openAiGenerateModal"
          >
            <i class="fa fa-magic" /> Create with AI
          </b-button>
          <!-- AI features aren't launched: visible as an upsell, nothing opens (2026-09-16). -->
          <b-button
            v-else-if="aiFormsComingSoon"
            data-test="ai-forms-create-coming-soon"
            variant="outline-secondary"
            class="create-form-btn mr-2"
            title="Create with AI is coming soon"
            disabled
          >
            <i class="fa fa-magic" /> Create with AI
            <span class="badge badge-light ml-1">Coming soon</span>
          </b-button>
          <b-tooltip v-if="aiFormsEnabled" target="create-ai-btn" placement="bottom" triggers="hover">
            Describe the form you want in plain English and let AI build a draft for you to review
          </b-tooltip>
          <b-button
            variant="success"
            class="create-form-btn"
            @click="gotoCreateForm"
          >
            <i class="fa fa-plus" /> Create New Form
          </b-button>
          <ai-generate-form-modal v-if="aiFormsEnabled" ref="aiGenerateModal" :site-id="siteID" :templates="forms" />
          <!-- Forms settings — Manager / Admin / Super Admin. Gate OFF (today): the cog opens the medical-history update reminders modal and 🔗 the
               two-tab Procedure Forms editor. Gate ON (Forms Settings): ONE "Settings" button opens the four-tab editor on New Patients; 🔗 is gone. -->
          <template v-if="canSeeFormsSettings">
            <b-button
              v-if="formsSettingsOn"
              id="forms-settings-btn"
              data-test="forms-settings-btn"
              variant="outline-secondary"
              class="forms-settings-cog ml-2"
              @click="openFormsReminderSettings"
            >
              <i class="fa fa-cog" /> Settings
            </b-button>
            <b-button
              v-else
              id="forms-settings-btn"
              variant="outline-secondary"
              class="forms-settings-cog ml-2"
              @click="openFormsReminderSettings"
            >
              <i class="fa fa-cog" />
            </b-button>
            <b-tooltip target="forms-settings-btn" placement="bottom" triggers="hover">
              {{ formsSettingsOn ? 'Forms Settings — new patient forms, medical history updates, procedure mappings and post-op messages' : 'Forms settings — medical-history update reminders' }}
            </b-tooltip>
            <forms-update-reminder-settings v-if="!formsSettingsOn" ref="formsReminderSettings" :site-id="siteID" />

            <!-- Procedure forms & post-op instructions: the full-screen editor
                 (ProcedureFormsEditor, mounted by ConsentMappingModal). Same
                 consent-mapping blob as Tx Presenter → Settings → Consent Forms,
                 plus the post-op column, the shared message and the auto-send
                 switch. Drives the Patient Profile "Get Form QR" procedure table
                 and the post-op sender. -->
            <b-button
              v-if="!formsSettingsOn"
              id="forms-consent-mapping-btn"
              variant="outline-secondary"
              class="forms-settings-cog ml-2"
              @click="openConsentMapping"
            >
              <i class="fa fa-link" />
            </b-button>
            <b-tooltip v-if="!formsSettingsOn" target="forms-consent-mapping-btn" placement="bottom" triggers="hover">
              Procedure forms &amp; post-op instructions
            </b-tooltip>
            <consent-mapping-modal
              :open="consentMappingOpen"
              :site-id="siteID"
              :forms-settings="formsSettingsOn"
              :initial-tab="settingsInitialTab"
              @close="consentMappingOpen = false"
            />
          </template>
        </div>
      </div>

      <!-- Side by Side Container for Forms and Submissions -->
      <div class="forms-content-wrapper">
        <!-- Forms List Panel -->
        <div class="forms-panel">
          <div class="panel-card">
            <div class="panel-header">
              <h5 class="section-title">
                <i class="fa fa-file-text-o section-icon"></i>
                Your Forms
              </h5>
            </div>

            <!-- Filter Section -->
            <div class="filter-row">
              <div class="filter-container">
                <div class="search-box">
                  <i class="fa fa-search search-icon"></i>
                  <input
                    type="text"
                    v-model="formNameFilter"
                    placeholder="Filter by form name..."
                    class="search-input"
                    @input="filterForms"
                  />
                  <i v-if="formNameFilter" @click="clearFormFilter" class="fa fa-times-circle clear-icon"></i>
                </div>
              </div>
            </div>

            <div v-if="forms && forms.length === 0" class="empty-state">
              <p>No forms available yet. Please click "Create New Form" button above to get started</p>
            </div>

            <div v-if="forms && filteredForms.length === 0 && formNameFilter && forms.length > 0" class="empty-state">
              <p>No forms match your search criteria. <a href="#" @click.prevent="clearFormFilter">Clear filter</a></p>
            </div>

            <div v-if="forms && forms.length > 0" class="forms-table-container">
              <!-- Table Header -->
              <div class="forms-table-header forms-table-header-with-packets">
                <div class="sortable" @click="sortFormsBy('Name')">
                  Form Name
                  <i :class="getSortIcon('forms', 'Name')"></i>
                </div>
                <div class="lang-column-header hide-on-mobile">Lang</div>
                <div class="packet-column-header hide-on-mobile">Packets</div>
                <div class="sortable" @click="sortFormsBy('LastUpdated')">
                  Last Updated
                  <i :class="getSortIcon('forms', 'LastUpdated')"></i>
                </div>
                <div class="text-right">Actions</div>
              </div>

              <!-- Pinned Forms Section -->
              <template v-if="pinnedForms.length > 0">
                <div class="section-label pinned-section-label">
                  <i class="fa fa-thumb-tack"></i> Pinned
                </div>
                <div v-for="form in pinnedForms" :key="form._id" class="form-item pinned-form-item form-item-with-packets" @click="form._isPacket ? showPacketModal(form) : gotoConfigureForm(form.FormIDForWeb)">
                  <!-- Form Name -->
                  <div class="text-title-wrap">
                    <i :class="form._isPacket ? 'fa fa-files-o' : 'fa fa-file-text-o'" aria-hidden="true"></i>
                    <div>
                      {{ form.Name }}
                      <span v-if="form._isPacket" class="language-badge packet-badge">PACKET</span>
                      <!-- Language badges shown inline on mobile only (columns hidden) -->
                      <span v-for="lang in getFormTranslationLanguages(form)" :key="'m-'+lang" class="language-badge language-badge-translated show-on-mobile-only" :title="`${lang.toUpperCase()} translations enabled`">{{ lang.toUpperCase() }}</span>
                    </div>
                  </div>

                  <!-- Languages Column -->
                  <div class="lang-column hide-on-mobile">
                    <span v-for="lang in getFormTranslationLanguages(form)" :key="lang" class="language-badge language-badge-translated" :title="`${lang.toUpperCase()} translations enabled`">{{ lang.toUpperCase() }}</span>
                  </div>

                  <!-- Part of Packet? -->
                  <div class="packet-column hide-on-mobile" @click.stop>
                    <template v-if="!form._isPacket && form.packets && form.packets.length > 0">
                      <span v-for="pkt in form.packets" :key="pkt.packetID" :id="`pkt-${form.FormIDForWeb}-${pkt.packetID}`" class="packet-icon-wrapper">
                        <i class="fa fa-files-o packet-membership-icon"></i>
                        <b-tooltip :target="`pkt-${form.FormIDForWeb}-${pkt.packetID}`" placement="top" triggers="hover">{{ pkt.packetName }}</b-tooltip>
                      </span>
                    </template>
                  </div>

                  <!-- Last Updated -->
                  <div class="date-column">{{formatDateTimeWitTz(form.LastUpdated, allSettings.GENERAL_TIMEZONE.PropertyValue)}}</div>

                  <!-- Actions -->
                  <div class="actions-container" @click.stop>
                    <template v-if="form._isPacket">
                      <i :id="`modify-packet-${form.PacketID}`" aria-hidden="true" class="fa fa-pencil action-icon" @click="showPacketModal(form)"/>
                      <b-tooltip :target="`modify-packet-${form.PacketID}`" placement="top" triggers="hover">Modify Packet</b-tooltip>

                      <i :id="`delete-packet-${form.PacketID}`" class="fa fa-trash action-icon" @click="confirmDeletePacket(form)"/>
                      <b-tooltip :target="`delete-packet-${form.PacketID}`" placement="top" triggers="hover">Delete Packet</b-tooltip>

                      <i :id="`get-short-url-packet-${form.PacketID}`" aria-hidden="true" class="fa fa-link action-icon" @click="showModal('select-patient', form.PacketID, true)"/>
                      <b-tooltip :target="`get-short-url-packet-${form.PacketID}`" placement="top" triggers="hover">Get Short URL For Packet</b-tooltip>

                      <i v-if="pinningFormId === form.PacketID" class="fa fa-spinner fa-spin action-icon pin-loading"/>
                      <i v-else :id="`pin-packet-${form.PacketID}`" aria-hidden="true" class="fa fa-thumb-tack action-icon pinned" @click="handleTogglePinPacket(form)"/>
                      <b-tooltip v-if="pinningFormId !== form.PacketID" :target="`pin-packet-${form.PacketID}`" placement="top" triggers="hover">Unpin Packet</b-tooltip>
                    </template>
                    <template v-else>
                      <i v-if="$can('delete','forms')" :id="`modify-form-${form.FormIDForWeb}`" aria-hidden="true" class="fa fa-pencil action-icon" @click="gotoConfigureForm(form.FormIDForWeb)"/>
                      <b-tooltip class="tool_tip" :target="`modify-form-${form.FormIDForWeb}`" placement="top" triggers="hover">
                        Modify Form
                      </b-tooltip>

                      <i v-if="$can('delete','forms')" :id="`delete-form-${form.FormIDForWeb}`" class="fa fa-trash action-icon" :class="{ 'action-icon-disabled': form.IsProtected }" @click="!form.IsProtected && showModal('delete', form.FormIDForWeb)"/>
                      <b-tooltip class="tool_tip" :target="`delete-form-${form.FormIDForWeb}`" placement="top" triggers="hover">
                        {{ form.IsProtected ? 'This form is protected and cannot be deleted' : 'Delete Form' }}
                      </b-tooltip>

                      <i :id="`get-short-url-${form.FormIDForWeb}`" aria-hidden="true" class="fa fa-link action-icon" @click="showModal('select-patient', form.FormIDForWeb)"/>
                      <b-tooltip class="tool_tip" :target="`get-short-url-${form.FormIDForWeb}`" placement="top" triggers="hover">
                        Get Short URL For the Form
                      </b-tooltip>

                      <i v-if="pinningFormId === form.FormIDForWeb" class="fa fa-spinner fa-spin action-icon pin-loading"/>
                      <i v-else :id="`pin-form-${form.FormIDForWeb}`" aria-hidden="true" class="fa fa-thumb-tack action-icon pinned" @click="handleTogglePin(form)"/>
                      <b-tooltip v-if="pinningFormId !== form.FormIDForWeb" class="tool_tip" :target="`pin-form-${form.FormIDForWeb}`" placement="top" triggers="hover">
                        Unpin Form
                      </b-tooltip>
                    </template>
                  </div>
                </div>
              </template>

              <!-- All Forms Section -->
              <template v-if="unpinnedForms.length > 0">
                <div v-if="pinnedForms.length > 0" class="section-label all-forms-label">
                  All Forms
                </div>
                <div v-for="form in unpinnedForms" :key="form._id" class="form-item form-item-with-packets" @click="form._isPacket ? showPacketModal(form) : gotoConfigureForm(form.FormIDForWeb)">
                  <!-- Form Name -->
                  <div class="text-title-wrap">
                    <i :class="form._isPacket ? 'fa fa-files-o' : 'fa fa-file-text-o'" aria-hidden="true"></i>
                    <div>
                      {{ form.Name }}
                      <span v-if="form._isPacket" class="language-badge packet-badge">PACKET</span>
                      <!-- Language badges shown inline on mobile only (columns hidden) -->
                      <span v-for="lang in getFormTranslationLanguages(form)" :key="'m-'+lang" class="language-badge language-badge-translated show-on-mobile-only" :title="`${lang.toUpperCase()} translations enabled`">{{ lang.toUpperCase() }}</span>
                    </div>
                  </div>

                  <!-- Languages Column -->
                  <div class="lang-column hide-on-mobile">
                    <span v-for="lang in getFormTranslationLanguages(form)" :key="lang" class="language-badge language-badge-translated" :title="`${lang.toUpperCase()} translations enabled`">{{ lang.toUpperCase() }}</span>
                  </div>

                  <!-- Part of Packet? -->
                  <div class="packet-column hide-on-mobile" @click.stop>
                    <template v-if="!form._isPacket && form.packets && form.packets.length > 0">
                      <span v-for="pkt in form.packets" :key="pkt.packetID" :id="`pkt-u-${form.FormIDForWeb}-${pkt.packetID}`" class="packet-icon-wrapper">
                        <i class="fa fa-files-o packet-membership-icon"></i>
                        <b-tooltip :target="`pkt-u-${form.FormIDForWeb}-${pkt.packetID}`" placement="top" triggers="hover">{{ pkt.packetName }}</b-tooltip>
                      </span>
                    </template>
                  </div>

                  <!-- Last Updated -->
                  <div class="date-column">{{formatDateTimeWitTz(form.LastUpdated, allSettings.GENERAL_TIMEZONE.PropertyValue)}}</div>

                  <!-- Actions -->
                  <div class="actions-container" @click.stop>
                    <template v-if="form._isPacket">
                      <i :id="`modify-packet-u-${form.PacketID}`" aria-hidden="true" class="fa fa-pencil action-icon" @click="showPacketModal(form)"/>
                      <b-tooltip :target="`modify-packet-u-${form.PacketID}`" placement="top" triggers="hover">Modify Packet</b-tooltip>

                      <i :id="`delete-packet-u-${form.PacketID}`" class="fa fa-trash action-icon" @click="confirmDeletePacket(form)"/>
                      <b-tooltip :target="`delete-packet-u-${form.PacketID}`" placement="top" triggers="hover">Delete Packet</b-tooltip>

                      <i :id="`get-short-url-packet-u-${form.PacketID}`" aria-hidden="true" class="fa fa-link action-icon" @click="showModal('select-patient', form.PacketID, true)"/>
                      <b-tooltip :target="`get-short-url-packet-u-${form.PacketID}`" placement="top" triggers="hover">Get Short URL For Packet</b-tooltip>

                      <i v-if="pinningFormId === form.PacketID" class="fa fa-spinner fa-spin action-icon pin-loading"/>
                      <i v-else :id="`pin-packet-u-${form.PacketID}`" aria-hidden="true" class="fa fa-thumb-tack action-icon" @click="handleTogglePinPacket(form)"/>
                      <b-tooltip v-if="pinningFormId !== form.PacketID" :target="`pin-packet-u-${form.PacketID}`" placement="top" triggers="hover">Pin Packet</b-tooltip>
                    </template>
                    <template v-else>
                      <i v-if="$can('delete','forms')" :id="`modify-form-${form.FormIDForWeb}`" aria-hidden="true" class="fa fa-pencil action-icon" @click="gotoConfigureForm(form.FormIDForWeb)"/>
                      <b-tooltip class="tool_tip" :target="`modify-form-${form.FormIDForWeb}`" placement="top" triggers="hover">
                        Modify Form
                      </b-tooltip>

                      <i v-if="$can('delete','forms')" :id="`delete-form-${form.FormIDForWeb}`" class="fa fa-trash action-icon" :class="{ 'action-icon-disabled': form.IsProtected }" @click="!form.IsProtected && showModal('delete', form.FormIDForWeb)"/>
                      <b-tooltip class="tool_tip" :target="`delete-form-${form.FormIDForWeb}`" placement="top" triggers="hover">
                        {{ form.IsProtected ? 'This form is protected and cannot be deleted' : 'Delete Form' }}
                      </b-tooltip>

                      <i :id="`get-short-url-${form.FormIDForWeb}`" aria-hidden="true" class="fa fa-link action-icon" @click="showModal('select-patient', form.FormIDForWeb)"/>
                      <b-tooltip class="tool_tip" :target="`get-short-url-${form.FormIDForWeb}`" placement="top" triggers="hover">
                        Get Short URL For the Form
                      </b-tooltip>

                      <i v-if="pinningFormId === form.FormIDForWeb" class="fa fa-spinner fa-spin action-icon pin-loading"/>
                      <i v-else :id="`pin-form-${form.FormIDForWeb}`" aria-hidden="true" class="fa fa-thumb-tack action-icon" @click="handleTogglePin(form)"/>
                      <b-tooltip v-if="pinningFormId !== form.FormIDForWeb" class="tool_tip" :target="`pin-form-${form.FormIDForWeb}`" placement="top" triggers="hover">
                        Pin Form
                      </b-tooltip>
                    </template>
                  </div>
                </div>
              </template>
            </div>

            <!-- Forms Pagination -->
            <div v-if="formsForDisplay.length > formsPerPage" class="pagination-container">
              <div class="pagination-info">
                Showing {{ formsStartIndex + 1 }}-{{ Math.min(formsStartIndex + formsPerPage, formsForDisplay.length) }} of {{ formsForDisplay.length }}
              </div>
              <div class="pagination-controls">
                <button class="pagination-btn" :disabled="formsCurrentPage === 1" @click="formsCurrentPage = 1">
                  <i class="fa fa-angle-double-left"></i>
                </button>
                <button class="pagination-btn" :disabled="formsCurrentPage === 1" @click="formsCurrentPage--">
                  <i class="fa fa-angle-left"></i>
                </button>
                <span class="pagination-page">Page {{ formsCurrentPage }} of {{ formsTotalPages }}</span>
                <button class="pagination-btn" :disabled="formsCurrentPage === formsTotalPages" @click="formsCurrentPage++">
                  <i class="fa fa-angle-right"></i>
                </button>
                <button class="pagination-btn" :disabled="formsCurrentPage === formsTotalPages" @click="formsCurrentPage = formsTotalPages">
                  <i class="fa fa-angle-double-right"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Form Submissions Panel -->
        <div class="submissions-panel">
          <div class="panel-card">
            <div class="panel-header">
              <h5 class="section-title">
                <i class="fa fa-clipboard section-icon"></i>
                Your Form Submissions
              </h5>
            </div>

            <!-- Advanced Filters Section -->
            <div class="advanced-filters-container">
              <div class="filters-wrapper">
                <!-- Submission Name Filter -->
                <div class="filter-group">
                  <div class="search-box">
                    <i class="fa fa-search search-icon"></i>
                    <input
                      type="text"
                      v-model="submissionNameFilter"
                      placeholder="Filter by submission name..."
                      class="search-input"
                      @input="filterSubmissions"
                    />
                    <i v-if="submissionNameFilter" @click="clearSubmissionFilter" class="fa fa-times-circle clear-icon"></i>
                  </div>
                </div>
            <!-- Form Type Filter -->
            <div class="filter-group">
              <multiselect
                v-model="formTypeFilter"
                :options="formTypeOptions"
                :multiple="false"
                :close-on-select="true"
                :clear-on-select="false"
                :preserve-search="true"
                placeholder="Filter by form type"
                class="filter-select"
                @input="applyFilters"
              >
                <template slot="option" slot-scope="props">
                  <div class="option-item">
                    <i class="fa fa-file-text-o"></i>
                    {{ props.option }}
                  </div>
                </template>
              </multiselect>
            </div>

            <!-- Patient Filter -->
            <div class="filter-group">
              <multiselect
                v-model="patientFilter"
                :options="patientOptions"
                :multiple="false"
                :close-on-select="true"
                :clear-on-select="false"
                :preserve-search="true"
                :custom-label="patientCustomLabel"
                label="Patient"
                track-by="PatNum"
                placeholder="Filter by patient"
                class="filter-select"
                @input="applyFilters"
              >
                <template slot="option" slot-scope="props">
                  <div class="option-item">
                    <div
                      class="avatar"
                      :class="getAvatarClass(props.option.PatNum)"
                      :style="getPatientAvatar(props.option.PatNum) ? `background-image: url(${getPatientAvatar(props.option.PatNum)})` : ''"
                    >
                      {{ !getPatientAvatar(props.option.PatNum) ? getInitials(props.option.Patient) : '' }}
                    </div>
                    <span>{{ props.option.Patient }}</span>
                  </div>
                </template>
              </multiselect>
            </div>

            <!-- Date Submitted Filter -->
            <div class="filter-group">
              <DatePicker
                v-model="dateSubmittedFilter"
                lang="en"
                format="MM/DD/YYYY"
                placeholder="Filter by date submitted"
                input-class="date-filter-input"
                @change="applyFilters"
                append-to-body
                class="filter-datepicker"
              >
                <i class="fa fa-calendar" slot="calendar-icon"></i>
              </DatePicker>
            </div>

            <!-- Date Updated Filter -->
            <div class="filter-group">
              <DatePicker
                v-model="dateUpdatedFilter"
                lang="en"
                format="MM/DD/YYYY"
                placeholder="Filter by date updated"
                input-class="date-filter-input"
                @change="applyFilters"
                append-to-body
                class="filter-datepicker"
              >
                <i class="fa fa-calendar" slot="calendar-icon"></i>
              </DatePicker>
            </div>

            <!-- Clear Filters Button -->
            <button
              class="clear-filters"
              @click="clearAllFilters"
              :disabled="!hasActiveFilters"
            >
              <i class="fa fa-times"></i>
              Clear Filters
            </button>
          </div>
        </div>

        <div v-if="formSubmissions && formSubmissions.length === 0" class="empty-state">
          <p>No submissions available yet</p>
        </div>

        <div v-if="formSubmissions && filteredSubmissions.length === 0 && hasActiveFilters && formSubmissions.length > 0" class="empty-state no-results-message">
          <div class="no-results-content">
            <i class="fa fa-filter no-results-icon"></i>
            <h4 class="no-results-title">No form submissions match your filters</h4>
            <p class="no-results-text">Try adjusting your filter criteria or <button class="clear-all-button" @click="clearAllFilters">clear all filters</button></p>
          </div>
        </div>

            <div v-if="formSubmissions && formSubmissions.length > 0" class="forms-table-container submissions-table">
              <!-- Table Header -->
              <div class="submissions-table-header">
                <div class="sortable" @click="sortSubmissionsBy('Name')">
                  Form Name
                  <i :class="getSortIcon('submissions', 'Name')"></i>
                </div>
                <div>Type</div>
                <div>Patient</div>
                <div class="sortable" @click="sortSubmissionsBy('DateSubmitted')">
                  Submitted
                  <i :class="getSortIcon('submissions', 'DateSubmitted')"></i>
                </div>
                <div class="sortable" @click="sortSubmissionsBy('DateUpdated')">
                  Updated
                  <i :class="getSortIcon('submissions', 'DateUpdated')"></i>
                </div>
                <div class="text-right">Actions</div>
              </div>

              <!-- Pinned Submissions Section -->
              <template v-if="pinnedSubmissions.length > 0">
                <div class="section-label pinned-section-label">
                  <i class="fa fa-thumb-tack"></i> Pinned
                </div>
                <div v-for="submission in pinnedSubmissions" :key="submission.FormSubmissionIDForWeb" class="submission-item pinned-submission-item">
                  <!-- Form Name -->
                  <div class="text-title-wrap" @click="gotoViewSubmission(submission.FormSubmissionIDForWeb)" style="cursor: pointer;">
                    <i class="fa fa-file-text-o" aria-hidden="true"></i>
                    <div>
                      {{ submission.Name }}
                      <span v-if="submission.Language && submission.Language !== 'en'" class="language-badge language-badge-submission" :title="`Submitted in ${submission.Language.toUpperCase()}`">{{ submission.Language.toUpperCase() }}</span>
                    </div>
                  </div>

                  <!-- Form Type -->
                  <div class="type-column">{{ submission.FormType }}</div>

                  <!-- Patient with Avatar: show linked name OR inline link editor when not linked -->
                  <div>
                    <div v-if="submission.PatNum" class="d-flex align-items-center">
                      <div
                        class="avatar"
                        :class="getAvatarClass(submission.PatNum)"
                        :style="getPatientAvatar(submission.PatNum) ? `background-image: url(${getPatientAvatar(submission.PatNum)})` : ''"
                      >
                        {{ !getPatientAvatar(submission.PatNum) ? getInitials(getPatientWithDOB(submission.PatNum)) : '' }}
                      </div>
                      <router-link :to="{ name: 'patientprofile', params: {patientID: `${siteID}-${submission.PatNum}`} }" target="_blank" class="patient-link" @click.stop>
                        {{getPatientWithDOB(submission.PatNum) }}
                      </router-link>
                    </div>
                    <div v-else>
                      <PatientInlineLink :siteID="siteID" :submission="submission" @linked="onSubmissionLinked" @open-link-modal="showLinkModal('link-to-patient', $event)" />
                    </div>
                  </div>

                  <!-- Date Submitted -->
                  <div class="date-column">{{formatDate(submission.DateSubmitted, allSettings.GENERAL_TIMEZONE.PropertyValue)}}</div>

                  <!-- Date Updated -->
                  <div class="date-column">{{formatDate(submission.DateUpdated, allSettings.GENERAL_TIMEZONE.PropertyValue)}}</div>

                  <!-- Actions -->
                  <div class="actions-container" @click.stop>
                    <a v-if="!submission.PatNum" :id="`link-submission-${submission.FormSubmissionIDForWeb}`" href="#" class="action-icon-link" @click.prevent="showLinkModal('link-to-patient', submission.FormSubmissionIDForWeb)">
                      <i class="fa fa-link action-icon"/>
                    </a>
                    <b-tooltip v-if="!submission.PatNum" class="tool_tip" :target="`link-submission-${submission.FormSubmissionIDForWeb}`" placement="top" triggers="hover">
                      Link to Patient
                    </b-tooltip>

                    <i v-if="pinningSubmissionId === submission.FormSubmissionIDForWeb" class="fa fa-spinner fa-spin action-icon pin-loading"/>
                    <i v-else :id="`pin-submission-${submission.FormSubmissionIDForWeb}`" aria-hidden="true" class="fa fa-thumb-tack action-icon pinned" @click="handleTogglePinSubmission(submission)"/>
                    <b-tooltip v-if="pinningSubmissionId !== submission.FormSubmissionIDForWeb" class="tool_tip" :target="`pin-submission-${submission.FormSubmissionIDForWeb}`" placement="top" triggers="hover">
                      Unpin Submission
                    </b-tooltip>
                  </div>
                </div>
              </template>

              <!-- All Submissions Section -->
              <template v-if="unpinnedSubmissions.length > 0">
                <div v-if="pinnedSubmissions.length > 0" class="section-label all-forms-label">
                  All Submissions
                </div>
                <div v-for="submission in unpinnedSubmissions" :key="submission.FormSubmissionIDForWeb" class="submission-item">
                  <!-- Form Name -->
                  <div class="text-title-wrap" @click="gotoViewSubmission(submission.FormSubmissionIDForWeb)" style="cursor: pointer;">
                    <i class="fa fa-file-text-o" aria-hidden="true"></i>
                    <div>
                      {{ submission.Name }}
                      <span v-if="submission.Language && submission.Language !== 'en'" class="language-badge language-badge-submission" :title="`Submitted in ${submission.Language.toUpperCase()}`">{{ submission.Language.toUpperCase() }}</span>
                    </div>
                  </div>

                  <!-- Form Type -->
                  <div class="type-column">{{ submission.FormType }}</div>

                  <!-- Patient with Avatar: show linked name OR inline link editor when not linked -->
                  <div>
                    <div v-if="submission.PatNum" class="d-flex align-items-center">
                      <div
                        class="avatar"
                        :class="getAvatarClass(submission.PatNum)"
                        :style="getPatientAvatar(submission.PatNum) ? `background-image: url(${getPatientAvatar(submission.PatNum)})` : ''"
                      >
                        {{ !getPatientAvatar(submission.PatNum) ? getInitials(getPatientWithDOB(submission.PatNum)) : '' }}
                      </div>
                      <router-link :to="{ name: 'patientprofile', params: {patientID: `${siteID}-${submission.PatNum}`} }" target="_blank" class="patient-link" @click.stop>
                        {{getPatientWithDOB(submission.PatNum) }}
                      </router-link>
                    </div>
                    <div v-else>
                      <PatientInlineLink :siteID="siteID" :submission="submission" @linked="onSubmissionLinked" @open-link-modal="showLinkModal('link-to-patient', $event)" />
                    </div>
                  </div>

                  <!-- Date Submitted -->
                  <div class="date-column">{{formatDate(submission.DateSubmitted, allSettings.GENERAL_TIMEZONE.PropertyValue)}}</div>

                  <!-- Date Updated -->
                  <div class="date-column">{{formatDate(submission.DateUpdated, allSettings.GENERAL_TIMEZONE.PropertyValue)}}</div>

                  <!-- Actions -->
                  <div class="actions-container" @click.stop>
                    <a v-if="!submission.PatNum" :id="`link-submission-${submission.FormSubmissionIDForWeb}`" href="#" class="action-icon-link" @click.prevent="showLinkModal('link-to-patient', submission.FormSubmissionIDForWeb)">
                      <i class="fa fa-link action-icon"/>
                    </a>
                    <b-tooltip v-if="!submission.PatNum" class="tool_tip" :target="`link-submission-${submission.FormSubmissionIDForWeb}`" placement="top" triggers="hover">
                      Link to Patient
                    </b-tooltip>

                    <i v-if="pinningSubmissionId === submission.FormSubmissionIDForWeb" class="fa fa-spinner fa-spin action-icon pin-loading"/>
                    <i v-else :id="`pin-submission-${submission.FormSubmissionIDForWeb}`" aria-hidden="true" class="fa fa-thumb-tack action-icon" @click="handleTogglePinSubmission(submission)"/>
                    <b-tooltip v-if="pinningSubmissionId !== submission.FormSubmissionIDForWeb" class="tool_tip" :target="`pin-submission-${submission.FormSubmissionIDForWeb}`" placement="top" triggers="hover">
                      Pin Submission
                    </b-tooltip>
                  </div>
                </div>
              </template>
            </div>

            <!-- Submissions Pagination -->
            <div v-if="submissionsForDisplay.length > submissionsPerPage" class="pagination-container">
              <div class="pagination-info">
                Showing {{ submissionsStartIndex + 1 }}-{{ Math.min(submissionsStartIndex + submissionsPerPage, submissionsForDisplay.length) }} of {{ submissionsForDisplay.length }}
              </div>
              <div class="pagination-controls">
                <button class="pagination-btn" :disabled="submissionsCurrentPage === 1" @click="submissionsCurrentPage = 1">
                  <i class="fa fa-angle-double-left"></i>
                </button>
                <button class="pagination-btn" :disabled="submissionsCurrentPage === 1" @click="submissionsCurrentPage--">
                  <i class="fa fa-angle-left"></i>
                </button>
                <span class="pagination-page">Page {{ submissionsCurrentPage }} of {{ submissionsTotalPages }}</span>
                <button class="pagination-btn" :disabled="submissionsCurrentPage === submissionsTotalPages" @click="submissionsCurrentPage++">
                  <i class="fa fa-angle-right"></i>
                </button>
                <button class="pagination-btn" :disabled="submissionsCurrentPage === submissionsTotalPages" @click="submissionsCurrentPage = submissionsTotalPages">
                  <i class="fa fa-angle-double-right"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <b-modal ref="delete" id="delete" :title="formReferences.length > 0 ? '⚠️ Warning: Form In Use' : 'Delete Form'" body-class="bg-white" hide-header-close centered>
        <div v-if="checkingReferences" class="text-center py-3">
          <i class="fa fa-spinner fa-spin"></i> Checking if any templates or pages link to this form...
        </div>
        <div v-else-if="formReferences.length > 0">
          <div class="delete-warning-banner">
            <i class="fa fa-exclamation-triangle"></i>
            <strong>This form is referenced by the following templates:</strong>
          </div>
          <ul class="delete-reference-list">
            <li v-for="ref in formReferences" :key="ref.id">
              <span class="ref-type-badge">{{ ref.type }}</span>
              <a :href="'/app/wiki-edit/' + ref.id" target="_blank" class="ref-link">{{ ref.name }} <i class="fa fa-external-link"></i></a>
            </li>
          </ul>
          <p class="mt-2 mb-2" style="font-size: 0.9em; color: #666;">Deleting this form will cause broken links in the templates listed above.</p>
          <div class="shared-links-warning">
            <i class="fa fa-chain-broken"></i>
            <div>
              <strong>Shared links will stop working</strong>
              <p>If you've shared this form's link with anyone, those links will stop working. If you haven't shared it, this is safe to delete.</p>
            </div>
          </div>
          <label class="delete-acknowledge-label">
            <input type="checkbox" v-model="deleteAcknowledged" />
            I understand these templates will have broken links
          </label>
        </div>
        <div v-else>
          <p>Are you sure you want to delete this form?</p>
          <div class="shared-links-warning">
            <i class="fa fa-chain-broken"></i>
            <div>
              <strong>Shared links will stop working</strong>
              <p>If you've shared this form's link with anyone, those links will stop working. If you haven't shared it, this is safe to delete.</p>
            </div>
          </div>
        </div>
        <div slot="modal-footer" class="w-100">
          <b-button variant="outline-secondary" size="sm" class="float-right" @click="hideModal('delete')">Cancel</b-button>
          <b-button v-if="formReferences.length > 0" variant="danger" size="sm" class="float-right mr-3" :disabled="!deleteAcknowledged || checkingReferences" @click="deleteSelected">Delete Anyway</b-button>
          <b-button v-else variant="danger" size="sm" class="float-right mr-3" :disabled="checkingReferences" @click="deleteSelected">Delete</b-button>
        </div>
      </b-modal>

      <b-modal
        ref="select-patient"
        id="select-patient"
        title="Get Form Link"
        body-class="bg-white mb-0"
        hide-header-close
      >
        <!-- Loading overlay for Get Link/QR operations -->
        <div v-if="loadingGetLink || loadingGetQR" class="modal-loading-overlay">
          <div class="modal-loading-content">
            <i class="fa fa-spinner fa-spin modal-loading-spinner"></i>
            <p class="modal-loading-text">{{ loadingGetLink ? 'Generating link...' : 'Generating QR code...' }}</p>
          </div>
        </div>

        <!-- Two jobs, two tabs (lib/linkMode.js buildLinkRequest). "Send to a patient" is the default and
             needs a patient: a patient new to the practice but already in Open Dental belongs HERE, not
             on a blank website link (that link arrives "Not linked" and has to be linked by hand). -->
        <div class="link-target-tabs" role="tablist" data-test="link-target-tabs">
          <button
            type="button"
            role="tab"
            class="link-target-tab"
            :class="{ active: linkTarget === 'patient' }"
            :aria-selected="linkTarget === 'patient' ? 'true' : 'false'"
            data-test="link-target-patient"
            @click="setLinkTarget('patient')"
          >
            <i class="fa fa-user mr-1" aria-hidden="true"></i> Send to a patient
          </button>
          <button
            type="button"
            role="tab"
            class="link-target-tab"
            :class="{ active: linkTarget === 'website' }"
            :aria-selected="linkTarget === 'website' ? 'true' : 'false'"
            data-test="link-target-website"
            @click="setLinkTarget('website')"
          >
            <i class="fa fa-globe mr-1" aria-hidden="true"></i> Website or QR code
          </button>
        </div>

        <div v-if="linkTarget === 'patient'" class="link-target-panel" data-test="link-target-patient-panel">
          <p class="link-target-help">
            For anyone already in Open Dental, new or existing. Their information fills in and the form
            files to their chart.
          </p>
          <multiselect
            v-model="selectedPatient"
            :options="patients"
            :multiple="false"
            :custom-label="patientCustomLabel"
            :taggable="false"
            :show-labels="false"
            track-by="Patient"
            placeholder="Search for a patient..."
            @select="onPatientChange"
            @remove="onPatientChange"
            :append-to-body="true"
          >
          <template slot="option" slot-scope="props">
            <div class="d-flex align-items-center">
              <span class="font-weight-medium">{{ patientCustomLabel(props.option) }}</span>
              <span class="text-muted ml-2 small">{{ patientDOB(props.option) }}</span>
            </div>
          </template>
          </multiselect>
          <div class="link-target-subline">
            <span v-if="!hasSelectedLinkPatient" class="link-target-hint" data-test="link-pick-patient-hint">Pick a patient to get their link.</span>
            <button
              type="button"
              class="btn btn-link link-refresh-patients"
              :disabled="refreshingLinkPatients"
              data-test="link-refresh-patients"
              @click="refreshLinkPatients"
            >
              <i v-if="refreshingLinkPatients" class="fa fa-spinner fa-spin mr-1" aria-hidden="true"></i>
              Can't find them? Refresh the patient list
            </button>
          </div>
        </div>

        <!-- Website / QR code only: who will use it? (lib/linkMode.js; Documents/forms-existing-patient-links.md D1) -->
        <div v-if="linkTarget === 'website'" class="link-mode-group" data-test="link-mode-group">
          <div class="link-mode-title">Who will use this link?</div>
          <b-form-radio-group
            v-model="linkMode"
            name="link-mode"
            stacked
            @change="onLinkModeChange"
          >
            <b-form-radio value="new" data-test="link-mode-new">
              <span class="link-mode-option">People NOT in Open Dental yet</span>
              <span class="link-mode-desc">Blank form. You link each submission to a patient afterwards.</span>
            </b-form-radio>
            <b-form-radio value="existing" data-test="link-mode-existing">
              <span class="link-mode-option">Patients already in Open Dental</span>
              <span class="link-mode-desc">They confirm date of birth and name, then enter a code texted to the phone on file. Their information fills in and it files to their chart.</span>
            </b-form-radio>
          </b-form-radio-group>
          <div class="link-mode-switch">
            Sending this to one patient?
            <button type="button" class="btn btn-link link-mode-switch-btn" data-test="link-switch-to-patient" @click="setLinkTarget('patient')">Use Send to a patient</button>
          </div>
          <div class="link-mode-note">Links already on your website keep working as before.</div>
        </div>

        <!-- Generated Link Display -->
        <div v-if="generatedLink" class="generated-link-container">
          <div class="link-success-message" data-test="generated-link-message">
            <i class="fa fa-check-circle"></i>
            {{ generatedLinkFor }} generated and copied to clipboard!
          </div>
          <div class="link-display-wrapper">
            <input
              type="text"
              class="link-input"
              :value="generatedLink"
              readonly
              ref="linkInput"
              @click="$refs.linkInput.select()"
            />
            <button class="copy-link-btn" @click="copyLinkToClipboard" title="Copy to clipboard">
              <i class="fa fa-copy"></i>
            </button>
          </div>
        </div>

        <div v-if="qrFor" class="qr-for-caption" data-test="qr-for-caption">QR code: {{ qrFor }}</div>
        <div class="qr-code-container" id="qr"></div>

        <div slot="modal-footer" class="w-100">
          <b-button
            variant="outline-secondary"
            size="sm"
            class="float-right"
            @click="closeSelectPatientModal"
            :disabled="loadingGetLink || loadingGetQR"
          >Close</b-button>
          <b-button
            variant="primary"
            size="sm"
            class="float-right mr-3"
            @click="getQRCodeForPatient($event)"
            :disabled="loadingGetQR || loadingGetLink || linkNeedsPatient"
            data-test="get-qr-code"
          >
            <i v-if="loadingGetQR" class="fa fa-spinner fa-spin mr-1"></i>
            Get QR Code
          </b-button>
          <b-button
            variant="success"
            size="sm"
            class="float-right mr-3"
            @click="getLinkForPatient($event)"
            :disabled="loadingGetLink || loadingGetQR || linkNeedsPatient"
            data-test="get-link"
          >
            <i v-if="loadingGetLink" class="fa fa-spinner fa-spin mr-1"></i>
            Get Link
          </b-button>
        </div>
        <PopupErrorDisplay/>
      </b-modal>

      <b-modal
        ref="link-to-patient"
        id="link-to-patient"
        title="Link Submission to a Patient"
        body-class="bg-white mb-0"
        hide-header-close
      >
        <b-row>
          <b-col>
            <label class="form-label">Select a Patient:</label>
            <div class="mt-2">
              <multiselect
                v-model="selectedPatient"
                :options="patients"
                :multiple="false"
                :taggable="false"
                :show-labels="false"
                label="Patient"
                track-by="Patient"
                placeholder="Search for a patient..."
                @select="resetError"
                :append-to-body="true"
              >
                <template slot="singleLabel" slot-scope="props">
                  <span class="d-flex align-items-center">
                    <span>{{ props.option.Patient }}</span>
                  </span>
                </template>
                <template slot="option" slot-scope="props">
                  <div class="d-flex align-items-center">
                    <span class="font-weight-medium">{{ props.option.Patient }}</span>
                  </div>
                </template>
              </multiselect>
            </div>
          </b-col>
        </b-row>

        <div slot="modal-footer" class="w-100">
          <b-button
            variant="outline-secondary"
            size="sm"
            class="float-right"
            @click="selectedPatient=[]; hideModal('link-to-patient')"
          >Cancel</b-button>
          <b-button
            variant="success"
            size="sm"
            class="float-right mr-3"
            @click="_linkSubmissionToPatient($event)"
            :disabled="loading"
          >Link Patient</b-button>
        </div>
        <PopupErrorDisplay/>
      </b-modal>

      <!-- Create/Edit Packet Modal -->
      <b-modal
        ref="packet-modal"
        id="packet-modal"
        :title="editingPacket ? 'Edit Packet' : 'Create a Packet'"
        size="lg"
        scrollable
        body-class="bg-white mb-0"
        hide-header-close
        @hidden="resetPacketModal"
      >
        <div class="packet-modal-body">
          <!-- Packet Name -->
          <div class="mb-3">
            <label class="form-label font-weight-bold">Packet Name <span class="text-danger">*</span></label>
            <input
              type="text"
              class="form-control"
              v-model="packetName"
              placeholder="e.g., New Patient Packet"
            />
          </div>

          <!-- New-patient packet designation.
               The whole label is the click target, not just the 12px box. -->
          <div class="mb-3">
            <label class="np-packet-toggle">
              <input type="checkbox" v-model="packetIsNewPatient" />
              <span>This is our new-patient packet</span>
            </label>
            <small class="form-text text-muted np-packet-hint">
              Schedule View flags a new patient on the day's schedule when this packet has not been
              sent to them. Only one packet can be the new-patient packet — ticking this clears it
              from any other.
            </small>
          </div>

          <!-- ONE merged PDF in ONE Open Dental imaging category for this packet, overriding each
               form's own "Save PDF to Open Dental Imaging module under Category". Same full-label
               click target as the new-patient toggle above. -->
          <div class="mb-3" data-test="packet-merge-pdf">
            <label class="np-packet-toggle">
              <input type="checkbox" v-model="packetMergePdf" data-test="packet-merge-pdf-toggle" />
              <span>Merge all forms submitted within this packet into a single PDF</span>
            </label>
            <small class="form-text text-muted np-packet-hint">
              When a patient fills out this packet, Open Dental gets one combined PDF under the
              category below instead of one PDF per form under each form's own category. A form sent
              on its own still uses its own category. Uploaded photos and files keep each form's own
              category.
            </small>

            <div v-if="packetMergePdf" class="packet-merge-fields">
              <label class="packet-merge-label" :class="{ 'text-danger': packetMergeValidation.categoryMissing }">
                Save the merged PDF to Open Dental Imaging module under Category: <span class="text-danger">*</span>
              </label>
              <select
                class="form-control"
                :class="{ 'is-invalid': packetMergeValidation.categoryMissing }"
                v-model="packetMergePdfCategory"
                data-test="packet-merge-pdf-category"
              >
                <option value="">--Please Select--</option>
                <option
                  v-for="item in packetPdfCategories"
                  :key="item.DefNum"
                  :value="item.DefNum"
                  v-text="item.ItemName"
                ></option>
              </select>
              <small v-if="loadingPacketPdfCategories" class="form-text text-muted np-packet-hint">Loading Open Dental categories…</small>
              <small v-else-if="!packetPdfCategories.length" class="form-text text-danger np-packet-hint">
                No Open Dental imaging categories were found for this office.
              </small>
              <small v-else-if="packetMergeValidation.categoryMissing" class="form-text text-danger np-packet-hint">
                Required — pick where the merged PDF goes in Open Dental.
              </small>

              <label
                class="packet-merge-label mt-3"
                :class="{ 'text-danger': packetMergeValidation.fileNameMissing || packetMergeValidation.fileNameTooLong }"
              >
                File name for the merged PDF: <span class="text-danger">*</span>
              </label>
              <input
                type="text"
                class="form-control"
                :class="{ 'is-invalid': packetMergeValidation.fileNameMissing || packetMergeValidation.fileNameTooLong }"
                v-model="packetMergePdfFileName"
                :maxlength="mergePdfFileNameMax"
                placeholder="e.g., new-patient-packet"
                data-test="packet-merge-pdf-filename"
              />
              <small v-if="packetMergeValidation.fileNameMissing" class="form-text text-danger np-packet-hint">Required</small>
              <small class="form-text text-muted np-packet-hint">
                Open Dental saves it with the time and date added, for example
                <strong>{{ mergePdfFileNameExample }}</strong>
              </small>
            </div>
          </div>

          <!-- Form Selector -->
          <div class="mb-3">
            <label class="form-label font-weight-bold">Add Forms to Packet</label>
            <multiselect
              v-model="packetFormToAdd"
              :options="availableFormsForPacket"
              :multiple="false"
              :taggable="false"
              :show-labels="false"
              label="Name"
              track-by="FormIDForWeb"
              placeholder="Search for a form to add..."
              @select="addFormToPacket"
              :append-to-body="true"
            />
          </div>

          <!-- Selected Forms List -->
          <div v-if="packetSelectedForms.length > 0" class="mb-3">
            <label class="form-label font-weight-bold">Forms in Packet ({{ packetSelectedForms.length }})</label>
            <div class="packet-forms-list">
              <div v-for="(form, index) in packetSelectedForms" :key="form.FormIDForWeb" class="packet-form-item">
                <div class="packet-form-order">{{ index + 1 }}</div>
                <div class="packet-form-name">{{ form.Name }}</div>
                <div class="packet-form-actions">
                  <button v-if="index > 0" class="btn btn-sm btn-outline-secondary packet-move-btn" @click="moveFormInPacket(index, -1)" title="Move up">
                    <i class="fa fa-arrow-up"></i>
                  </button>
                  <button v-if="index < packetSelectedForms.length - 1" class="btn btn-sm btn-outline-secondary packet-move-btn" @click="moveFormInPacket(index, 1)" title="Move down">
                    <i class="fa fa-arrow-down"></i>
                  </button>
                  <button class="btn btn-sm btn-outline-danger packet-move-btn" @click="removeFormFromPacket(index)" title="Remove">
                    <i class="fa fa-times"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div v-if="packetSelectedForms.length > 0 && packetSelectedForms.length < 2" class="text-warning small">
            <i class="fa fa-exclamation-triangle"></i> A packet must contain at least 2 forms.
          </div>
        </div>

        <div slot="modal-footer" class="w-100">
          <b-button variant="outline-secondary" size="sm" class="float-right" @click="hideModal('packet-modal')">Cancel</b-button>
          <b-button
            variant="success"
            size="sm"
            class="float-right mr-3"
            @click="savePacketHandler"
            :disabled="!packetName || packetSelectedForms.length < 2 || !packetMergeValidation.ok || savingPacket"
          >
            <i v-if="savingPacket" class="fa fa-spinner fa-spin mr-1"></i>
            {{ editingPacket ? 'Update Packet' : 'Save Packet' }}
          </b-button>
        </div>
      </b-modal>

      <!-- Delete Packet Confirmation Modal -->
      <b-modal
        ref="delete-packet"
        id="delete-packet"
        title="Delete Packet"
        body-class="bg-white mb-0"
        hide-header-close
      >
        <p>Are you sure you want to delete this packet? The individual forms will not be deleted.</p>
        <div slot="modal-footer" class="w-100">
          <b-button variant="outline-secondary" size="sm" class="float-right" @click="hideModal('delete-packet')">Cancel</b-button>
          <b-button variant="danger" size="sm" class="float-right mr-3" @click="deletePacketHandler">Delete</b-button>
        </div>
      </b-modal>
    </div>
  </div>
</template>

<script>
/* eslint-disable */

import Vue from 'vue';
import Multiselect from "vue-multiselect";
import Widget from "@/components/Widget/Widget";
import { mapState, mapActions, mapGetters } from 'vuex';
import PopupErrorDisplay from "@/components/ErrorDisplay/PopupErrorDisplay";
import mixin from '../../../mixin.js';
import moment from 'moment-timezone';
import QRCode from "qrcode";
import AdminTable from '@/pages/Admin/AdminTable';
import BigStat from '../../../components/BigStat/BigStat';
import DatePicker from "vue2-datepicker";
import FormsHomeSkeleton from './FormsHomeSkeleton.vue';
import FeatureRequestButton from '@/components/FeatureRequestButton/FeatureRequestButton.vue';
import FeatureRequestNote from '@/components/FeatureRequestButton/FeatureRequestNote.vue';
import PatientInlineLink from '@/components/PatientInlineLink.vue';
import FormsUpdateReminderSettings from './FormsUpdateReminderSettings.vue';
import ConsentMappingModal from './ConsentMappingModal.vue';
import FollowupsUiToggle from '@/components/Followups/FollowupsUiToggle.vue';
import { switchUi } from '@/utils/followups/uiSwitch.js';
import { formsSettingsUiOn } from '@/utils/formsSettings/gate';
import { settingsTabFromQuery } from '@/utils/formsSettings/host';
import AiGenerateFormModal from './AiGenerateFormModal.vue';
import { MERGE_PDF_FILE_NAME_MAX, packetFileNameSlug, validatePacketMerge, buildPacketMergeFields, readPacketMergeState } from '../libraries/packet-merge-pdf';
import { extractApiError } from '@/utils/apiError';
import { defaultLinkMode, buildLinkRequest } from '../lib/linkMode';

export default {
  name: "FormsHome",
  components: { Widget, Multiselect, PopupErrorDisplay, AdminTable, BigStat, DatePicker, FormsHomeSkeleton, FeatureRequestButton, FeatureRequestNote, PatientInlineLink, FormsUpdateReminderSettings, ConsentMappingModal, AiGenerateFormModal, FollowupsUiToggle },
  mixins: [mixin],

  computed: {
    ...mapState('forms', ['patients']),
    ...mapState('settings', ['allSettings']),
    ...mapGetters('settings', ['siteID'])
  },

  created() {
    if(this.allSettings)
        this.init();
  },

  mounted() {

  },

  data() {
    return {
      // Procedure -> consent form mapping modal (prop-driven scrim, matching the
      // Insurance Verification modal pattern rather than b-modal).
      consentMappingOpen: false,
      // Forms Settings (release gate 'formsSettings'): which tab the editor opens on, and a counter that re-reads the Super Admin's own switch.
      settingsInitialTab: 'newPatients',
      formsSettingsVersion: 0,

      filteredForms: [],
      filteredSubmissions: [],
      formNameFilter: '',
      submissionNameFilter: '',

      // New filter properties
      formTypeFilter: null,
      patientFilter: null,
      dateSubmittedFilter: null,
      dateUpdatedFilter: null,

      forms: [],
      formCategories: [],
      formSubmissions: [],
      filteredForms: [],
      filteredSubmissions: [],
      formNameFilter: '',
      submissionNameFilter: '',
      selectedFormID: null,
      formReferences: [],
      checkingReferences: false,
      deleteAcknowledged: false,
      selectedPatient: [],
      selectedFormSubmissionIDForWeb: null,
      formColumns: ['Name', 'LastUpdated', 'Actions'],
      // Avatar colors for patients without images
      avatarColors: [
        'bg-primary text-white',
        'bg-success text-white',
        'bg-info text-white',
        'bg-warning text-white',
        'bg-danger text-white',
        'bg-gray text-white',
        'bg-gray-dark text-white',
        'bg-gray-light text-white'
      ],
      // Sorting state for Forms table
      formsSortColumn: 'LastUpdated',
      formsSortDirection: 'desc',
      
      // Sorting state for Submissions table
      submissionsSortColumn: 'DateUpdated',
      submissionsSortDirection: 'desc',

      // Pagination state for Forms
      formsCurrentPage: 1,
      formsPerPage: 10,

      // Pagination state for Submissions
      submissionsCurrentPage: 1,
      submissionsPerPage: 10,

      // Loading state
      busy: false,
      loadingGetLink: false, // Loading state for Get Link button
      loadingGetQR: false, // Loading state for Get QR Code button
      generatedLink: null, // Stores the generated form link to display in modal

      // Pin loading state
      pinningFormId: null,
      pinningSubmissionId: null,

      // Packet state
      packets: [],
      packetName: '',
      packetIsNewPatient: false,   // designates THE new-patient packet (one per site)
      // "Merge all forms submitted within this packet into a single PDF" — ONE PDF, ONE Open Dental
      // imaging category, overriding each form's own (libraries/packet-merge-pdf.js; forms-service
      // lib/packetPdfBatch.js does the merging).
      packetMergePdf: false,
      packetMergePdfCategory: '',   // OD image-category DefNum; '' = --Please Select--
      packetMergePdfFileName: '',
      packetPdfCategories: [],      // loaded once, the first time the box is ticked
      loadingPacketPdfCategories: false,
      packetSelectedForms: [],
      packetFormToAdd: null,
      editingPacket: null,  // null = create mode, object = edit mode
      savingPacket: false,
      selectedPacketForDelete: null,
      isPacketLink: false,  // Track if Get Link modal is for a packet
      linkMode: 'new',      // Get Link modal, generic links only: 'new' | 'existing' (re-defaulted on every open)
      linkTarget: 'patient', // Get Link modal tab: 'patient' (default, patient required) | 'website' (reset on every open)
      generatedLinkFor: '',  // Get Link modal: who the link on screen is for ("Link for Jane Doe")
      qrFor: '',             // Get Link modal: who the QR code on screen is for
      refreshingLinkPatients: false,

      formOptions: {
        filterable: false,
        skin:'table',
        sortByColumn: true,
        sortable: ['DateSubmitted', 'DateUpdated'],
        perPage: 50,
        pagination: { chunk: 50, dropdown: false },
        filterByColumn: true,  // https://matanya.gitbook.io/vue-tables-2/list-filters
        filterable: ['Name', 'DateSubmitted', 'DateUpdated', ],
        headings: {
          Name: 'Form Name',
          DateSubmitted: 'Date Submitted',
          DateUpdated: 'Date Updated'
        },
      },
      formSubmissionOptions: {
        filterable: false,
        skin:'table',
        sortByColumn: true,
        sortable: ['Name', 'FormType', 'Patient', 'DateSubmitted', 'DateUpdated'],
        perPage: 50,
        pagination: { chunk: 50, dropdown: false },
        filterByColumn: true,  // https://matanya.gitbook.io/vue-tables-2/list-filters
        filterable: ['Name', 'Patient', 'DateSubmitted', 'DateUpdated', ],
        headings: {
          Name: 'Form Name',
          Patient: 'Patient',
          DateSubmitted: 'Date Submitted',
          DateUpdated: 'Date Updated'
        },
      }
    };
  },
  methods: {
    ...mapActions('alert', ['setDismissSuccessCountDown', 'setErrored', 'setMessage', 'setLoading', 'setLoaded']),
    ...mapActions('forms', ['getForms', 'getFormSubmissions', 'deleteForm', 'checkFormReferences', 'getFormUrl', 'linkSubmissionToPatient', 'togglePinForm', 'togglePinSubmission', 'savePacket', 'getPackets', 'deletePacket', 'togglePinPacket', 'getPacketUrl']),

    async init() {
      this.busy = true;
      // Wall-clock timer so we can see the speedup in CloudWatch /
      // user reports without having to wire up a separate APM. Safe
      // to leave in — console.time is a no-op if devtools aren't open.
      console.time('[FormsHome] init');
      try {
        // Parallelize the three independent fetches. Previously these
        // ran sequentially (await getForms → await getFormSubmissions →
        // await getPackets), which added every call's latency. None of
        // the three depends on another, so Promise.all collapses the
        // total wait to max(T1, T2, T3) instead of T1 + T2 + T3.
        //
        // getForms: pass includeConfigData:false so the backend returns
        //   the list-view projection (scalars for language badges + field
        //   count, NO ConfigData blob). On a mature site this shrinks
        //   the response from multi-MB to ~tens of KB. Backend still
        //   defaults to true for every other caller (bulkCopyForms,
        //   mobile, stale frontend tabs) — opt-out only.
        // getFormSubmissions: already opts out of the heavy fields.
        const [formsRes, submissionsRes, packetsRes] = await Promise.all([
          this.getForms({ siteID: this.siteID, includeConfigData: false }),
          this.getFormSubmissions({
            siteID: this.siteID,
            includeFormData: false,
            includeConfigData: false,
          }),
          this.getPackets({ siteID: this.siteID }),
        ]);

        this.forms = formsRes.forms;
        this.formCategories = formsRes.categories;
        this.formSubmissions = submissionsRes;
        this.packets = packetsRes || [];

        // Merge packets into forms list as virtual form items
        this.mergePacketsIntoForms();

        // Reset filters
        this.filteredForms = [];
        this.filteredSubmissions = [];
        this.formNameFilter = '';
        this.submissionNameFilter = '';

        // Apply default sorting (DateUpdated descending for submissions, LastUpdated descending for forms)
        this.applySortingToForms();
        this.applySortingToSubmissions();
      } catch(err) {
        console.error(err);
      } finally {
        this.busy = false;
        console.timeEnd('[FormsHome] init');
      }
    },



    // Filter methods
    filterForms() {
      if (!this.formNameFilter.trim()) {
        this.filteredForms = [];
        return;
      }

      const searchTerm = this.formNameFilter.toLowerCase().trim();
      this.filteredForms = this.forms.filter(form =>
        form.Name.toLowerCase().includes(searchTerm)
      );
    },

    filterSubmissions() {
      this.applyFilters();
    },

    applyFilters() {
      // Start with all submissions
      let filtered = [...this.formSubmissions];

      // Apply name filter
      if (this.submissionNameFilter && this.submissionNameFilter.trim()) {
        const searchTerm = this.submissionNameFilter.toLowerCase().trim();
        filtered = filtered.filter(submission =>
          submission.Name.toLowerCase().includes(searchTerm)
        );
      }

      // Apply form type filter
      if (this.formTypeFilter) {
        filtered = filtered.filter(submission =>
          submission.FormType === this.formTypeFilter
        );
      }

      // Apply patient filter
      if (this.patientFilter) {
        filtered = filtered.filter(submission =>
          submission.PatNum === this.patientFilter.PatNum
        );
      }

      // Apply date submitted filter
      if (this.dateSubmittedFilter) {
        const filterDate = moment(this.dateSubmittedFilter).startOf('day');
        filtered = filtered.filter(submission => {
          const submissionDate = moment(submission.DateSubmitted).startOf('day');
          return submissionDate.isSame(filterDate);
        });
      }

      // Apply date updated filter
      if (this.dateUpdatedFilter) {
        const filterDate = moment(this.dateUpdatedFilter).startOf('day');
        filtered = filtered.filter(submission => {
          const updatedDate = moment(submission.DateUpdated).startOf('day');
          return updatedDate.isSame(filterDate);
        });
      }

      // Update filtered submissions
      this.filteredSubmissions = filtered;
    },

    clearFormFilter() {
      this.formNameFilter = '';
      this.filteredForms = [];
    },

    async handleTogglePin(form) {
      if (this.pinningFormId) return; // Prevent double-clicks

      const newPinState = !form.IsPinned;
      this.pinningFormId = form.FormIDForWeb;

      try {
        await this.togglePinForm({
          formIDForWeb: form.FormIDForWeb,
          siteID: this.siteID,
          isPinned: newPinState
        });
        // Update local state
        form.IsPinned = newPinState ? 1 : 0;
        // Re-sort to reflect the new pin status
        this.applySortingToForms();
      } catch (err) {
        console.error('Error toggling pin:', err);
      } finally {
        this.pinningFormId = null;
      }
    },

    async handleTogglePinSubmission(submission) {
      if (this.pinningSubmissionId) return; // Prevent double-clicks

      const newPinState = !submission.IsPinned;
      this.pinningSubmissionId = submission.FormSubmissionIDForWeb;

      try {
        await this.togglePinSubmission({
          formSubmissionIDForWeb: submission.FormSubmissionIDForWeb,
          siteID: this.siteID,
          isPinned: newPinState
        });
        // Update local state
        submission.IsPinned = newPinState ? 1 : 0;
        // Re-sort to reflect the new pin status
        this.applySortingToSubmissions();
      } catch (err) {
        console.error('Error toggling pin submission:', err);
      } finally {
        this.pinningSubmissionId = null;
      }
    },

    onSubmissionLinked(payload) {
      const { submissionID, patient } = payload || {};
      if (!submissionID) return;
      // Update the matching submission in local state
      const sub = (this.formSubmissions || []).find(s => s.FormSubmissionIDForWeb === submissionID);
      if (sub) {
        sub.PatNum = patient && patient.PatNum ? patient.PatNum : sub.PatNum;
      }
      // Refresh filters/sorting so UI reflects changes
      this.applyFilters();
      this.applySortingToSubmissions();
    },

    clearSubmissionFilter() {
      this.submissionNameFilter = '';
      this.applyFilters();
    },

    clearAllFilters() {
      this.submissionNameFilter = '';
      this.formTypeFilter = null;
      this.patientFilter = null;
      this.dateSubmittedFilter = null;
      this.dateUpdatedFilter = null;
      this.filteredSubmissions = [];
    },

    // Sorting methods
    getSortIcon(table, column) {
      const sortColumn = table === 'forms' ? this.formsSortColumn : this.submissionsSortColumn;
      const sortDirection = table === 'forms' ? this.formsSortDirection : this.submissionsSortDirection;
      
      if (sortColumn !== column) {
        return 'fa fa-sort';
      }
      return sortDirection === 'asc' ? 'fa fa-sort-asc' : 'fa fa-sort-desc';
    },

    sortFormsBy(column) {
      // Toggle direction if same column, otherwise default to descending for dates, ascending for text
      if (this.formsSortColumn === column) {
        this.formsSortDirection = this.formsSortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.formsSortColumn = column;
        // Default to descending for dates, ascending for text
        this.formsSortDirection = column === 'LastUpdated' ? 'desc' : 'asc';
      }
      this.applySortingToForms();
    },

    sortSubmissionsBy(column) {
      // Toggle direction if same column, otherwise default to descending for dates, ascending for text
      if (this.submissionsSortColumn === column) {
        this.submissionsSortDirection = this.submissionsSortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.submissionsSortColumn = column;
        // Default to descending for dates, ascending for text
        this.submissionsSortDirection = (column === 'DateSubmitted' || column === 'DateUpdated') ? 'desc' : 'asc';
      }
      this.applySortingToSubmissions();
    },

    applySortingToForms() {
      const column = this.formsSortColumn;
      const direction = this.formsSortDirection;

      this.forms.sort((a, b) => {
        // Pinned forms always come first
        const aPinned = a.IsPinned ? 1 : 0;
        const bPinned = b.IsPinned ? 1 : 0;
        if (aPinned !== bPinned) {
          return bPinned - aPinned; // Pinned forms first
        }

        // Within same pin status, apply normal sorting
        let valA = a[column];
        let valB = b[column];

        // Handle date columns
        if (column === 'LastUpdated') {
          valA = valA ? new Date(valA).getTime() : 0;
          valB = valB ? new Date(valB).getTime() : 0;
        } else {
          // String comparison (case-insensitive)
          valA = (valA || '').toString().toLowerCase();
          valB = (valB || '').toString().toLowerCase();
        }

        if (valA < valB) return direction === 'asc' ? -1 : 1;
        if (valA > valB) return direction === 'asc' ? 1 : -1;
        return 0;
      });

      // Force Vue to re-render the list
      this.forms = [...this.forms];
    },

    applySortingToSubmissions() {
      const column = this.submissionsSortColumn;
      const direction = this.submissionsSortDirection;

      this.formSubmissions.sort((a, b) => {
        // Pinned submissions always come first
        const aPinned = a.IsPinned ? 1 : 0;
        const bPinned = b.IsPinned ? 1 : 0;
        if (aPinned !== bPinned) {
          return bPinned - aPinned; // Pinned submissions first
        }

        // Within same pin status, apply normal sorting
        let valA = a[column];
        let valB = b[column];

        // Handle date columns
        if (column === 'DateSubmitted' || column === 'DateUpdated') {
          valA = valA ? new Date(valA).getTime() : 0;
          valB = valB ? new Date(valB).getTime() : 0;
        } else {
          // String comparison (case-insensitive)
          valA = (valA || '').toString().toLowerCase();
          valB = (valB || '').toString().toLowerCase();
        }

        if (valA < valB) return direction === 'asc' ? -1 : 1;
        if (valA > valB) return direction === 'asc' ? 1 : -1;
        return 0;
      });

      // Force Vue to re-render the list
      this.formSubmissions = [...this.formSubmissions];

      // Re-apply filters if any are active
      if (this.hasActiveFilters) {
        this.applyFilters();
      }
    },

    // Returns the list of non-English languages a form has translations for
    // (used to render the little "ES" / "RU" badges next to the form name).
    //
    // Three input paths in priority order — covers backend projection
    // (fast path) AND both rollback scenarios:
    //   1. form.SupportedLanguages  — new backend scalar (JSON array string
    //      OR parsed array, depending on MySQL driver); preferred when present.
    //   2. form.SpanishEnabled      — legacy flag also projected by the new
    //      backend for forms saved before supportedLanguages existed.
    //   3. form.ConfigData          — stale backend that hasn't been deployed
    //      yet, OR a future caller that still passes the full blob. We still
    //      parse it so nothing breaks during the deploy transition.
    getFormTranslationLanguages(form) {
      if (!form) return [];

      // Path 1: backend-projected SupportedLanguages scalar (fast path)
      if (form.SupportedLanguages != null) {
        try {
          // MySQL's JSON_EXTRACT can return either a parsed array or a
          // JSON-encoded string depending on the driver/serverless-mysql
          // version. Accept both.
          const langs = typeof form.SupportedLanguages === 'string'
            ? JSON.parse(form.SupportedLanguages)
            : form.SupportedLanguages;
          if (Array.isArray(langs)) {
            return langs.filter(l => l !== 'en');
          }
        } catch (_) { /* fall through to other paths */ }
      }

      // Path 2: backend-projected SpanishEnabled flag (legacy forms)
      if (form.SpanishEnabled === true || form.SpanishEnabled === 1 || form.SpanishEnabled === 'true') {
        return ['es'];
      }

      // Path 3: fallback — parse ConfigData ourselves. Only kicks in when
      // the backend still ships ConfigData (pre-deploy rollback, or other
      // callers that pass includeConfigData:true).
      if (form.ConfigData) {
        try {
          const configData = typeof form.ConfigData === 'string'
            ? JSON.parse(form.ConfigData)
            : form.ConfigData;

          const supported = configData.formConfig?.supportedLanguages;
          if (supported && Array.isArray(supported)) {
            return supported.filter(l => l !== 'en');
          }
          if (configData.formConfig?.spanishEnabled === true) {
            return ['es'];
          }
        } catch (e) {
          console.warn('Error parsing ConfigData for form:', form.FormIDForWeb, e);
        }
      }

      return [];
    },

    patientCustomLabel(option) {
      return option.Patient || `Patient ${option.PatNum}`;
    },

    patientDOB({Birthdate}) {
      return this.formatDate(Birthdate);
    },

    getPatientWithDOB(patNum) {
      let p = this.getPatient(patNum);
      if(p) {
        return p.PatientWithDOB;
      }
    },

    // Methods for handling avatars and initials
    getInitials(name) {
      if (!name) return '';

      const parts = name.split(' ');
      if (parts.length === 1) return parts[0].charAt(0);

      return parts[0].charAt(0) + parts[parts.length - 1].charAt(0);
    },

    getPatientAvatar(patientNum) {
      if (this.patients) {
        const patient = this.patients.find(p => p.PatNum === patientNum);
        // Check for valid PictureUrl - must exist, not be "null" string, and not be empty
        if (patient && patient.PictureUrl && 
            patient.PictureUrl !== 'null' && 
            patient.PictureUrl !== 'undefined' &&
            patient.PictureUrl.trim() !== '') {
          return patient.PictureUrl;
        }
      }
      return null;
    },

    getAvatarClass(patientNum) {
      if (this.getPatientAvatar(patientNum)) {
        return '';
      }

      if (!patientNum) return 'bg-gray-200 text-gray-700';

      let hash = 0;
      for (let i = 0; i < patientNum.toString().length; i++) {
        hash = patientNum.toString().charCodeAt(i) + ((hash << 5) - hash);
      }

      const colorIndex = Math.abs(hash) % this.avatarColors.length;
      return this.avatarColors[colorIndex];
    },

    async showModal(name, formIDForWeb, isPacket = false) {
      this.selectedFormID = formIDForWeb;
      this.isPacketLink = isPacket;
      this.resetError2();

      if (name === 'select-patient') {
        this.linkMode = this.defaultLinkModeFor(formIDForWeb, isPacket);
        // Always open on "Send to a patient"; nothing from the last open (a backdrop click skips
        // closeSelectPatientModal) may carry over.
        this.linkTarget = 'patient';
        this.selectedPatient = [];
        this.clearQrCanvas();
        this.clearGeneratedLink();
      }

      if (name === 'delete') {
        this.formReferences = [];
        this.deleteAcknowledged = false;
        this.checkingReferences = true;
        this.$refs[name].show();
        try {
          const result = await this.checkFormReferences({ formIDForWeb });
          this.formReferences = result.references || [];
        } catch (e) {
          console.log('Error checking references:', e);
          this.formReferences = [];
        }
        this.checkingReferences = false;
        return;
      }

      this.$refs[name].show();
    },

    showLinkModal(name, formSubmissionIDForWeb) {
      this.selectedFormSubmissionIDForWeb = formSubmissionIDForWeb;
      this.resetError2();
      this.$refs[name].show();
    },

    // Clear secondary error state used by modals (kept for backward compatibility)
    resetError2() {
      if (typeof this.setErrored2 === 'function') this.setErrored2(false);
      if (typeof this.setMessage2 === 'function') this.setMessage2('');
    },

    hideModal(ref) {
      this.$refs[ref].hide()
    },

    gotoCreateForm() {
      let routeData = this.$router.resolve(
        {
          path: `forms/create-form`
        }
      );
      window.open(routeData.href, '_blank');
    },

    openAiGenerateModal() {
      this.$refs.aiGenerateModal.show();
    },

    // The settings button. Gate OFF: today's medical-history modal. Gate ON: the four-tab Forms Settings editor on New Patients.
    openFormsReminderSettings() {
      if (this.formsSettingsOn) {
        this.openFormsSettings('newPatients');
        return;
      }
      this.$refs.formsReminderSettings.open();
    },

    // 🔗 (gate OFF only): the two-tab Procedure Forms editor, exactly as before.
    openConsentMapping() {
      this.consentMappingOpen = true;
    },

    // The Forms Settings editor, on one of its tabs ('newPatients' | 'medHx' | 'mappings' | 'postOp').
    openFormsSettings(tab) {
      this.settingsInitialTab = tab || 'newPatients';
      this.consentMappingOpen = true;
    },

    // Tx Presenter's "Open Forms Settings" link lands on /app/forms?settings=<tab>: open the editor on that tab once (gate ON only). The query is
    // left in place on purpose: Layout.vue keys <router-view> on $route.fullPath, so replacing it remounted this page and closed the editor
    // (found on dev 10/26). A refresh of that URL reopens the editor - the same trade as Conversations' ?roomId=.
    openSettingsFromRoute() {
      const route = this.$route;
      const tab = settingsTabFromQuery(route && route.query ? route.query.settings : undefined);
      if (!tab || !this.formsSettingsOn || !this.canSeeFormsSettings) return;
      this.openFormsSettings(tab);
    },

    // The Super Admin "New view" / "Classic view" link (an office user's switch reloads the page instead).
    setFormsSettingsLayout(target) {
      const how = switchUi('formsSettings', target, { isSuperAdmin: this.isSuperAdmin, userKey: this.userID });
      if (how === 'rerender') this.formsSettingsVersion += 1;
    },

    gotoViewForm(urlID) {
      let routeData = this.$router.resolve(
        {
          path: `/view-form/${urlID}`
        }
      );
      window.open(routeData.href, '_blank');
    },

    gotoViewSubmission(formSubmissionID) {
      let routeData = this.$router.resolve(
        {
          path: `forms/view-submission/${formSubmissionID}`
        }
      );
      window.open(routeData.href, '_blank');
    },

    gotoConfigureForm(formID) {

      /*
      this.$router.push({
        name: 'ViewWorkflow',
        params: {
          workflowID : workflowID
        }
      });
      */

      let routeData = this.$router.resolve(
        {
          path: `forms/edit-form/${formID}`
        }
      );
      window.open(routeData.href, '_blank');
    },

    async deleteSelected() {
      try {
        this.hideModal('delete');
        await this.deleteForm({formIDForWeb: this.selectedFormID});
        this.$toast.success('Form deleted successfully. Please wait while we refresh the page!');
        this.$router.go(0);
      } catch(e) {
        console.log('Error' + e);
        this.$toast.error(e.message);
      }
    },

    // What the Get Link / Get QR Code buttons send, from the tab (lib/linkMode.js buildLinkRequest).
    // "Send to a patient" needs a patient; "Website or QR code" never sends one.
    currentLinkRequest() {
      return buildLinkRequest({
        linkTarget: this.linkTarget,
        patNum: this.selectedPatient && this.selectedPatient.PatNum ? this.selectedPatient.PatNum : null,
        linkMode: this.linkMode,
      });
    },

    // "Link for Jane Doe" / "Website link for people NOT in Open Dental yet" — who the link on screen is for.
    linkLabelFor(patNum, linkMode) {
      if (patNum) return `Link for ${this.patientCustomLabel(this.selectedPatient)}`;
      return linkMode === 'existing'
        ? 'Website link for patients already in Open Dental'
        : 'Website link for people NOT in Open Dental yet';
    },

    setLinkTarget(target) {
      if (target === this.linkTarget) return;
      this.linkTarget = target === 'website' ? 'website' : 'patient';
      // The link/QR on screen belongs to the other tab — never leave it showing.
      this.selectedPatient = [];
      this.clearQrCanvas();
      this.clearGeneratedLink();
      this.resetError2();
    },

    // The patient list is loaded at login, so a patient entered in Open Dental since then is missing —
    // exactly when staff would otherwise fall back to a blank website link.
    async refreshLinkPatients() {
      if (this.refreshingLinkPatients) return;
      this.refreshingLinkPatients = true;
      try {
        const list = await this.$store.dispatch('customer/getPatients', { siteID: this.siteID });
        if (Array.isArray(list) && list.length) {
          this.$toast.success(`Patient list refreshed (${list.length} patients)`);
        } else {
          this.$toast.error('Could not refresh the patient list. Please try again.');
        }
      } finally {
        this.refreshingLinkPatients = false;
      }
    },

    async getLinkForPatient() {
      const request = this.currentLinkRequest();
      if (request.error) {
        this.$toast.error(request.error);
        return;
      }
      // A patient-specific link sends no mode (undefined → omitted); a website link always does.
      const { patNum, linkMode } = request;

      this.loadingGetLink = true;
      // Clear any existing QR code when generating a new link
      this.clearQrCanvas();
      try {
        let res;
        if (this.isPacketLink) {
          res = await this.getPacketUrl({ siteID: this.siteID, packetID: this.selectedFormID, patNum, linkMode });
        } else {
          res = await this.getFormUrl({ siteID: this.siteID, formIDForWeb: this.selectedFormID, patNum, linkMode });
        }
        let shortUrl = res.data;
        // Store the link to display in the modal
        this.generatedLink = shortUrl;
        this.generatedLinkFor = this.linkLabelFor(patNum, linkMode);
        // Copy to clipboard
        await navigator.clipboard.writeText(shortUrl);
      } catch(err) {
        console.error(err);
        this.$toast.error(err.message || 'Failed to get link');
      } finally {
        // The patient stays selected: the link on screen says whose it is.
        this.loadingGetLink = false;
      }
    },

    clearQrCanvas() {
      this.qrFor = '';
      let canvas = document.getElementById('canvasid');
      if(canvas)
        canvas.parentNode.removeChild(canvas);
    },

    clearGeneratedLink() {
      this.generatedLink = null;
      this.generatedLinkFor = '';
    },

    closeSelectPatientModal() {
      this.selectedPatient = [];
      this.clearQrCanvas();
      this.clearGeneratedLink();
      this.hideModal('select-patient');
    },

    async copyLinkToClipboard() {
      if (!this.generatedLink) return;
      try {
        await navigator.clipboard.writeText(this.generatedLink);
        this.$toast.success('Link copied to clipboard!');
      } catch(err) {
        console.error('Failed to copy:', err);
        this.$toast.error('Failed to copy link');
      }
    },

    onPatientChange() {
      this.clearQrCanvas();
      this.clearGeneratedLink();
      this.resetError2();
    },

    // The link/QR on screen belongs to the OTHER mode once the radio changes — never leave it showing.
    onLinkModeChange() {
      this.clearQrCanvas();
      this.clearGeneratedLink();
      this.resetError2();
    },

    // Default "Who is this link for?" from the form's FormType, or for a packet from the FormTypes of
    // its forms (packet.FormIDs → the loaded forms list's FormType). Rules live in lib/linkMode.js.
    defaultLinkModeFor(id, isPacket) {
      const realForms = (this.forms || []).filter(f => !f._isPacket);
      if (isPacket) {
        const packet = (this.packets || []).find(p => p.PacketID === id)
          || (this.forms || []).find(f => f._isPacket && f.PacketID === id);
        let formIDs = packet ? packet.FormIDs : [];
        if (typeof formIDs === 'string') {
          try { formIDs = JSON.parse(formIDs); } catch (e) { formIDs = []; }
        }
        const packetFormTypes = (Array.isArray(formIDs) ? formIDs : [])
          .map(fid => { const f = realForms.find(x => x.FormIDForWeb === fid); return f ? f.FormType : null; })
          .filter(Boolean);
        return defaultLinkMode({ packetFormTypes });
      }
      const form = realForms.find(f => f.FormIDForWeb === id);
      return defaultLinkMode({ formType: form ? form.FormType : null });
    },

    async getQRCodeForPatient() {
      const request = this.currentLinkRequest();
      if (request.error) {
        this.$toast.error(request.error);
        return;
      }
      const { patNum, linkMode } = request;

      this.loadingGetQR = true;
      // Clear any existing generated link (and QR) when generating a new QR code
      this.clearGeneratedLink();
      this.clearQrCanvas();
      try {
        let res;
        if (this.isPacketLink) {
          res = await this.getPacketUrl({ siteID: this.siteID, packetID: this.selectedFormID, patNum, linkMode });
        } else {
          res = await this.getFormUrl({ siteID: this.siteID, formIDForWeb: this.selectedFormID, patNum, linkMode });
        }
        let shortUrl = res.data;
        var canvas = document.createElement('canvas');
        canvas.id = "canvasid";
        var qr = document.getElementById('qr');
        qr.appendChild(canvas);

        QRCode.toCanvas(canvas, shortUrl, (error) => {
          if (error) {
            console.error(error);
            this.$toast.error("Something went wrong trying to create the QR Code. Please try again.");
            return false;
          }
        });
        this.qrFor = this.linkLabelFor(patNum, linkMode);
      } catch(err) {
        console.error(err);
        this.$toast.error(err.message || 'Failed to generate QR code');
      } finally {
        // The patient stays selected: the caption says whose QR code it is.
        this.loadingGetQR = false;
      }
    },

    async _linkSubmissionToPatient() {
      if(this.selectedPatient.length === 0) {
        this.setErrored2(true);
        this.setMessage2("Please select a Patient to link this submission to");
        return false;
      }

      let payload = {
        siteID: this.siteID,
        formSubmissionIDForWeb: this.selectedFormSubmissionIDForWeb,
        patNum: this.selectedPatient.PatNum,
        // The Open Dental Comm Log note for this submission is written at link time (an unlinked
        // submission has no patient to note): who linked it + their OD user (forms-existing-patient-links.md).
        linkedByStaffName: this.user && this.user.Name ? this.user.Name : '',
        userODNum: this.user && this.user.ODUserNum ? this.user.ODUserNum : 0
      }
      console.dir(payload);
      try {
        let res = await this.linkSubmissionToPatient(payload);
        this.hideModal('link-to-patient');
        this.$router.go(0);
      } catch(err) {
        console.error(err);
      } finally {
        this.selectedPatient=[];
      }
    },

    // =============================================
    // PACKET METHODS
    // =============================================

    mergePacketsIntoForms() {
      // Remove any previously merged packets
      this.forms = this.forms.filter(f => !f._isPacket);

      // Add packets as virtual form items with _isPacket flag
      const packetItems = this.packets.map(p => ({
        _isPacket: true,
        _id: `packet-${p.PacketID}`,
        PacketID: p.PacketID,
        Name: p.Name,
        FormIDs: p.FormIDs,
        IsPinned: p.IsPinned,
        // ⛔ Must be carried over: showPacketModal reads it to tick the box, and the save ALWAYS
        // sends the box. Dropped here, a re-opened packet shows unticked and any save of it
        // (even a rename) silently clears the practice's designation.
        IsNewPatientPacket: p.IsNewPatientPacket,
        // ⛔ Same reason: showPacketModal reads these to show the merge setting, and the save ALWAYS
        // sends mergePdf. Dropped here, a re-opened packet shows the box unticked and any save of
        // it (even a rename) silently switches the office's merged PDF off.
        MergePdf: p.MergePdf,
        MergePdfCategory: p.MergePdfCategory,
        MergePdfFileName: p.MergePdfFileName,
        LastUpdated: p.LastUpdated,
        CreatedOn: p.CreatedOn,
        SiteID: p.SiteID,
        packets: []  // Packets don't belong to other packets
      }));

      // Also add _id to regular forms for v-for keying
      this.forms = this.forms.map(f => ({
        ...f,
        _isPacket: false,
        _id: `form-${f.FormIDForWeb}`
      }));

      this.forms = [...this.forms, ...packetItems];
    },

    showPacketModal(packet = null) {
      if (packet && packet._isPacket) {
        // Edit mode
        this.editingPacket = packet;
        this.packetName = packet.Name;
        // Absent on a packet saved before this field existed, which reads as false — correct.
        this.packetIsNewPatient = Number(packet.IsNewPatientPacket) === 1;
        // Absent on a packet saved before the merge setting existed, which reads as OFF — correct.
        const merge = readPacketMergeState(packet);
        this.packetMergePdf = merge.mergePdf;
        this.packetMergePdfCategory = merge.category;
        this.packetMergePdfFileName = merge.fileName;
        // Resolve form IDs to form objects
        this.packetSelectedForms = packet.FormIDs
          .map(fid => this.forms.find(f => !f._isPacket && f.FormIDForWeb === fid))
          .filter(Boolean);
      } else {
        // Create mode
        this.editingPacket = null;
        this.packetName = '';
        this.packetSelectedForms = [];
        this.packetIsNewPatient = false;
        this.packetMergePdf = false;
        this.packetMergePdfCategory = '';
        this.packetMergePdfFileName = '';
      }
      this.packetFormToAdd = null;
      this.$refs['packet-modal'].show();
    },

    resetPacketModal() {
      this.editingPacket = null;
      this.packetName = '';
      this.packetSelectedForms = [];
      this.packetFormToAdd = null;
      this.packetIsNewPatient = false;
      this.packetMergePdf = false;
      this.packetMergePdfCategory = '';
      this.packetMergePdfFileName = '';
      this.savingPacket = false;
    },

    // The same OD imaging-category list the form builder's "Save PDF to Open Dental Imaging module
    // under Category" dropdown uses (POST /get-form-categories → [{ DefNum, ItemName }]). Loaded
    // only when the merge box is ticked, and only once per page.
    async loadPacketPdfCategories() {
      if (this.packetPdfCategories.length || this.loadingPacketPdfCategories) return;
      this.loadingPacketPdfCategories = true;
      try {
        const r = await this.$store.dispatch('forms/getFormCategories', { siteID: this.siteID });
        this.packetPdfCategories = Array.isArray(r) ? r : (r && r.data) || [];
      } catch (e) {
        console.error('FormsHome: failed to load OD imaging categories for the packet merge:', e);
        this.$toast.error('Could not load Open Dental imaging categories.');
      } finally {
        this.loadingPacketPdfCategories = false;
      }
    },

    addFormToPacket(form) {
      if (!this.packetSelectedForms.find(f => f.FormIDForWeb === form.FormIDForWeb)) {
        this.packetSelectedForms.push(form);
      }
      this.$nextTick(() => { this.packetFormToAdd = null; });
    },

    removeFormFromPacket(index) {
      this.packetSelectedForms.splice(index, 1);
    },

    moveFormInPacket(index, direction) {
      const newIndex = index + direction;
      const items = [...this.packetSelectedForms];
      const [moved] = items.splice(index, 1);
      items.splice(newIndex, 0, moved);
      this.packetSelectedForms = items;
    },

    async savePacketHandler() {
      if (!this.packetName || this.packetSelectedForms.length < 2) return;
      if (!this.packetMergeValidation.ok) return;

      this.savingPacket = true;
      try {
        const payload = {
          siteID: this.siteID,
          name: this.packetName,
          formIDs: this.packetSelectedForms.map(f => f.FormIDForWeb),
          // Always sent, including false — this screen OWNS the field, so leaving it out would
          // make un-ticking the box do nothing. forms-service only skips the column when the key
          // is absent, which exists to stop OLDER clients wiping a designation on an unrelated
          // rename; it is not the behaviour we want here.
          isNewPatientPacket: this.packetIsNewPatient,
          // mergePdf / mergePdfCategory / mergePdfFileName. mergePdf is ALWAYS sent, including false,
          // for the same reason as isNewPatientPacket above (see libraries/packet-merge-pdf.js).
          ...buildPacketMergeFields({
            mergePdf: this.packetMergePdf,
            category: this.packetMergePdfCategory,
            fileName: this.packetMergePdfFileName
          })
        };

        if (this.editingPacket) {
          payload.packetID = this.editingPacket.PacketID;
        }

        await this.savePacket(payload);
        this.$toast.success(this.editingPacket ? 'Packet updated successfully!' : 'Packet created successfully!');
        this.hideModal('packet-modal');
        // Reload data
        await this.init();
      } catch(err) {
        console.error(err);
        // Show forms-service's reason when it gave one (e.g. "that Open Dental imaging category no
        // longer exists"), else the old generic message.
        this.$toast.error(extractApiError(err, 'Failed to save packet. Please try again.'));
      } finally {
        this.savingPacket = false;
      }
    },

    confirmDeletePacket(packet) {
      this.selectedPacketForDelete = packet;
      this.$refs['delete-packet'].show();
    },

    async deletePacketHandler() {
      if (!this.selectedPacketForDelete) return;
      try {
        await this.deletePacket({
          siteID: this.siteID,
          packetID: this.selectedPacketForDelete.PacketID
        });
        this.$toast.success('Packet deleted successfully!');
        this.hideModal('delete-packet');
        await this.init();
      } catch(err) {
        console.error(err);
        this.$toast.error('Failed to delete packet.');
      } finally {
        this.selectedPacketForDelete = null;
      }
    },

    async handleTogglePinPacket(packet) {
      if (this.pinningFormId) return;
      this.pinningFormId = packet.PacketID;
      try {
        await this.togglePinPacket({
          packetID: packet.PacketID,
          siteID: this.siteID
        });
        // Update local state
        const idx = this.forms.findIndex(f => f._isPacket && f.PacketID === packet.PacketID);
        if (idx !== -1) {
          this.$set(this.forms[idx], 'IsPinned', this.forms[idx].IsPinned ? 0 : 1);
        }
        this.applySortingToForms();
      } catch(err) {
        console.error(err);
      } finally {
        this.pinningFormId = null;
      }
    },

    /*
    formatDate(value, timezone) {
      let fmtStr = timezone && timezone.toUpperCase() === 'AFRICA/MASERU' ? 'DD/MM/YYYY' : 'MM/DD/YYYY';
      if(value && value !== null && value !== '')
          return moment(value).format(fmtStr);
      else
          return '';
    },

    formatDateTime(value, timezone) {
      let fmtStr = timezone && timezone.toUpperCase() === 'AFRICA/MASERU' ? 'DD/MM/YYYY h:mm a' : 'MM/DD/YYYY';
      if(value && value !== null && value !== '') {
        return moment(value).tz(timezone).format(fmtStr);
      }
      else
          return '';
    },
    */

    // Feature request event handlers
    onFeatureRequestSent(featureName) {
      this.$toast.success(`${featureName} request sent successfully! Our team will contact you soon to enable this feature.`);
    },

    onFeatureRequestError(error) {
      this.$toast.error(`Failed to send request: ${error.message || 'Please try again or contact support directly.'}`);
    },
  },

  computed: {
    // Get Link modal: a patient is picked → patient-specific link, the "Who is this link for?" group hides.
    hasSelectedLinkPatient() {
      return !!(this.selectedPatient && this.selectedPatient.PatNum);
    },

    // Get Link / Get QR Code stay disabled on "Send to a patient" until a patient is picked.
    linkNeedsPatient() {
      return this.linkTarget !== 'website' && !this.hasSelectedLinkPatient;
    },

    // Packet merged-PDF setting (libraries/packet-merge-pdf.js). Kept in THIS (the last) computed
    // block: this component declares `computed` twice and only the last one takes effect.
    packetMergeValidation() {
      return validatePacketMerge({
        mergePdf: this.packetMergePdf,
        category: this.packetMergePdfCategory,
        fileName: this.packetMergePdfFileName
      });
    },
    mergePdfFileNameMax() {
      return MERGE_PDF_FILE_NAME_MAX;
    },
    mergePdfFileNameExample() {
      const base = String(this.packetMergePdfFileName || '').trim() || 'new-patient-packet';
      return `${base}_${moment().format('hhmma')}_${moment().format('MM-DD-YYYY')}.pdf`;
    },

    ...mapState({
      patients: state => state.customer.patients
    }),

    ...mapGetters({
      'getPatient': 'customer/getPatient'
    }),

    // Gate for the Forms Update Reminder settings cog — Manager / Admin / Super Admin (not Employee/Outsourced).
    // NOTE: this component has TWO `computed:` blocks (the one at the top of options is shadowed by THIS one,
    // since duplicate object keys = last wins), so component-local computeds MUST go here to take effect.
    canSeeFormsSettings() {
      return !!(this.user && ['Super Admin', 'Admin', 'Manager'].includes(this.user.Role));
    },

    // The release gate for the one Forms Settings editor (src/utils/followups/uiVersion.js, area 'formsSettings', read through
    // src/utils/formsSettings/gate.js). false ⇒ today's screens: the cog opens the medical-history modal, 🔗 the two-tab editor. true ⇒ ONE "Settings"
    // button opens the four-tab editor and 🔗 is hidden. userID (the employee ID) is the office's own "classic Teamio" choice, once the area is past 'preview'.
    formsSettingsOn() {
      // eslint-disable-next-line no-unused-expressions
      this.formsSettingsVersion;
      return formsSettingsUiOn({ isSuperAdmin: this.isSuperAdmin, userKey: this.userID });
    },

    // The Super Admin wording on the classic <-> new link (an office user always reads the office wording).
    formsSettingsNote() {
      return this.formsSettingsOn
        ? 'Super Admin preview — offices still see the classic settings and procedure-forms screens.'
        : 'Super Admin: the new Forms Settings screen (New Patients, Medical History, mappings, post-op) is ready to check.';
    },

    enabled() {
      return this.forms.map(w => w.Active);
    },

    columns() {
      let cols = ['Name', 'FormType', 'Patient', 'DateSubmitted', 'DateUpdated', 'link'];
      return cols;
    },

    // Get unique form types from submissions
    formTypeOptions() {
      if (!this.formSubmissions || this.formSubmissions.length === 0) return [];

      const uniqueTypes = [...new Set(this.formSubmissions.map(submission => submission.FormType))];
      return uniqueTypes.filter(type => type); // Filter out any null/undefined values
    },

    // Get patient options from submissions
    patientOptions() {
      if (!this.formSubmissions || this.formSubmissions.length === 0) return [];

      // Get unique patient numbers
      const uniquePatNums = [...new Set(this.formSubmissions
        .filter(submission => submission.PatNum) // Filter out submissions without patients
        .map(submission => submission.PatNum))];

      // Map to patient objects. The roster can be empty mid-session (phi-mask
      // cache purge / localStorage quota drop) — never assume it's an array.
      return uniquePatNums.map(patNum => {
        const patient = (this.patients || []).find(p => p.PatNum === patNum);
        return patient || { PatNum: patNum, Patient: `Patient ${patNum}` };
      });
    },

    // Check if any filters are active
    hasActiveFilters() {
      return this.submissionNameFilter ||
             this.formTypeFilter ||
             this.patientFilter ||
             this.dateSubmittedFilter ||
             this.dateUpdatedFilter;
    },

    // Forms pagination computed properties
    formsForDisplay() {
      return this.filteredForms.length > 0 ? this.filteredForms : this.forms;
    },

    pinnedForms() {
      return this.paginatedForms.filter(form => form.IsPinned);
    },

    unpinnedForms() {
      return this.paginatedForms.filter(form => !form.IsPinned);
    },

    formsStartIndex() {
      return (this.formsCurrentPage - 1) * this.formsPerPage;
    },

    formsTotalPages() {
      return Math.ceil(this.formsForDisplay.length / this.formsPerPage) || 1;
    },

    paginatedForms() {
      const start = this.formsStartIndex;
      const end = start + this.formsPerPage;
      return this.formsForDisplay.slice(start, end);
    },

    // Submissions pagination computed properties
    submissionsForDisplay() {
      return this.filteredSubmissions.length > 0 ? this.filteredSubmissions : this.formSubmissions;
    },

    pinnedSubmissions() {
      return this.paginatedSubmissions.filter(submission => submission.IsPinned);
    },

    unpinnedSubmissions() {
      return this.paginatedSubmissions.filter(submission => !submission.IsPinned);
    },

    submissionsStartIndex() {
      return (this.submissionsCurrentPage - 1) * this.submissionsPerPage;
    },

    submissionsTotalPages() {
      return Math.ceil(this.submissionsForDisplay.length / this.submissionsPerPage) || 1;
    },

    paginatedSubmissions() {
      const start = this.submissionsStartIndex;
      const end = start + this.submissionsPerPage;
      return this.submissionsForDisplay.slice(start, end);
    },

    // Forms available for adding to a packet (exclude packets and already-selected forms)
    availableFormsForPacket() {
      const selectedIds = this.packetSelectedForms.map(f => f.FormIDForWeb);
      return this.forms
        .filter(f => !f._isPacket && !selectedIds.includes(f.FormIDForWeb))
        .sort((a, b) => {
          // Sort by LastUpdated desc, then alphabetical
          const dateA = new Date(a.LastUpdated || 0);
          const dateB = new Date(b.LastUpdated || 0);
          if (dateB - dateA !== 0) return dateB - dateA;
          return (a.Name || '').localeCompare(b.Name || '');
        });
    }
  },

  watch: {
    // Tx Presenter's "Open Forms Settings" link (/app/forms?settings=mappings): once the viewer is known to be allowed in, open the editor on that tab.
    canSeeFormsSettings: {
      immediate: true,
      handler(can) { if (can) this.openSettingsFromRoute(); },
    },

    // Ticking "merge into a single PDF" loads the category list and, when no file name is set yet,
    // prefills one from the packet name ("New Patient Packet" → "new-patient-packet"). Editable.
    packetMergePdf(on) {
      if (!on) return;
      this.loadPacketPdfCategories();
      if (!String(this.packetMergePdfFileName || '').trim()) {
        this.packetMergePdfFileName = packetFileNameSlug(this.packetName);
      }
    },
    // Keep an UNTOUCHED prefill in step with the packet name (ticked before the name was typed ⇒
    // "packet" would otherwise stick). A name the user edited by hand is never overwritten.
    packetName(newName, oldName) {
      if (!this.packetMergePdf) return;
      if (this.packetMergePdfFileName === packetFileNameSlug(oldName)) {
        this.packetMergePdfFileName = packetFileNameSlug(newName);
      }
    },

    allSettings(newValue, oldValue) {
      //console.log(`watch changed. Old value: ${JSON.stringify(oldValue)}, \n New value: ${JSON.stringify(newValue)}`);
      if(!oldValue) {
        this.init();
      }
    },

    enabled: {
     async handler() {
        // Watch handler for form changes
     },
    }
  }

};
</script>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style src="./FormsHomeModern.scss" lang="scss" scoped></style>

<style lang="scss" scoped>
/* Fix for multiselect dropdown positioning and z-index issues */

/* Basic multiselect styling */
::v-deep .multiselect {
  position: relative;
  min-height: 40px;
}

::v-deep .multiselect__tags {
  min-height: 40px;
  padding: 8px 40px 0 8px;
  border-radius: 4px;
  border: 1px solid #e3e6f0;
  background: #fff;
}

::v-deep .multiselect__single {
  padding-left: 5px;
  color: #495057;
  margin-bottom: 8px;
}

::v-deep .multiselect__placeholder {
  color: #6c757d;
  padding-left: 5px;
  margin-bottom: 8px;
}

/* Fix for dropdowns in modals - using append-to-body */
::v-deep .multiselect__content-wrapper {
  position: absolute;
  z-index: 10000 !important;
  background: #fff;
  border: 1px solid #e3e6f0;
  border-radius: 4px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  max-height: 300px;
  overflow-y: auto;
  width: 100%;
  min-width: 200px;
}

/* Option styling */
::v-deep .multiselect__option {
  padding: 10px 12px;
  min-height: 40px;
  line-height: 1.4;
  color: #495057;
  cursor: pointer;
  white-space: nowrap;
}

::v-deep .multiselect__option:hover {
  background: #f8f9fa;
  color: #16181b;
}

::v-deep .multiselect__option--highlight {
  background: #3abf94;
  color: #fff;
}

::v-deep .multiselect__option--selected {
  background: #f8f9fa;
  color: #495057;
  font-weight: 500;
}

/* Patient option with avatar styling */
::v-deep .option-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

::v-deep .option-item .avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 600;
  flex-shrink: 0;
  background-size: cover;
  background-position: center;
}

/* Filter multiselect specific styles */
::v-deep .filter-select {
  .multiselect__tags {
    min-height: 38px;
    padding: 6px 40px 0 6px;
    overflow: hidden;
  }

  .multiselect__single,
  .multiselect__placeholder {
    padding-left: 8px;
    margin-bottom: 6px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: calc(100% - 10px);
    display: block;
  }
}

/* Vue2-datepicker styles */
::v-deep .mx-datepicker {
  width: 100%;
  position: relative;
}

::v-deep .mx-input-wrapper {
  position: relative !important;
  display: block;
  width: 100%;
}

::v-deep .date-filter-input {
  height: 38px;
  padding: 0.375rem 2.5rem 0.375rem 0.75rem;
  font-size: 0.75rem;
  line-height: 1.5;
  color: #475569;
  background-color: #fff;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  width: 100%;

  &::placeholder {
    color: #64748b;
    opacity: 1;
    font-size: 0.75rem;
  }

  &:focus {
    border-color: #94a3b8;
    outline: 0;
    box-shadow: 0 0 0 2px rgba(148, 163, 184, 0.15);
  }
}

/* Calendar icon - targets the mx-input-append span (sibling of mx-input-wrapper) */
::v-deep .mx-input-append {
  position: absolute !important;
  right: 10px !important;
  top: 50% !important;
  transform: translateY(-50%) !important;
  color: #64748b !important;
  pointer-events: none;
  display: flex !important;
  align-items: center;
  justify-content: center;
  height: auto !important;
  width: auto !important;
  padding: 0 !important;
  margin: 0 !important;

  .fa-calendar {
    font-size: 13px;
  }
}

::v-deep .mx-icon-clear {
  display: none !important;
}

/* Modal specific fixes */
::v-deep .modal-body {
  overflow: visible;
  position: relative;
}

/* Ensure modals don't clip dropdowns */
::v-deep .modal {
  overflow: visible;
}

::v-deep .modal-dialog {
  overflow: visible;
}

::v-deep .modal-content {
  overflow: visible;
}

/* Modal loading overlay for Get Link/QR operations */
.modal-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  border-radius: 8px;
}

.modal-loading-content {
  text-align: center;
  padding: 30px;
}

.modal-loading-spinner {
  font-size: 48px;
  color: #3abf94;
  margin-bottom: 16px;
  display: block;
}

.modal-loading-text {
  font-size: 16px;
  color: #334155;
  font-weight: 500;
  margin: 0;
}

/* QR Code container styling */
.qr-code-container {
  text-align: center;
  margin: 20px 0;

  canvas {
    max-width: 100%;
    height: auto;
  }
}

/* Generated Link Display styling */
.generated-link-container {
  margin: 20px 0;
  padding: 16px;
  background-color: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 8px;
}

.link-success-message {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #166534;
  font-weight: 500;
  font-size: 14px;
  margin-bottom: 12px;

  i {
    font-size: 18px;
    color: #22c55e;
  }
}

.link-display-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
}

.link-input {
  flex: 1;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13px;
  font-family: monospace;
  background-color: white;
  color: #374151;
  cursor: text;

  &:focus {
    outline: none;
    border-color: #3abf94;
    box-shadow: 0 0 0 2px rgba(58, 191, 148, 0.2);
  }
}

.copy-link-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background-color: white;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background-color: #f9fafb;
    border-color: #3abf94;
    color: #3abf94;
  }

  &:active {
    background-color: #f0fdf4;
  }

  i {
    font-size: 16px;
  }
}

/* Link action styling */
.link-action {
  color: #3abf94;
  text-decoration: none;
  font-size: 14px;
  
  &:hover {
    color: #2d8f6f;
    text-decoration: underline;
  }
}

/* Forms Feature Request Styling */
.forms-feature-request {
  padding: 3rem 2rem;
  text-align: center;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 12px;
  margin: 2rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.feature-request-container {
  max-width: 600px;
  margin: 0 auto;
}

.feature-request-header {
  margin-bottom: 2rem;
}

.feature-request-header .feature-icon {
  font-size: 4rem;
  color: #547fff;
  margin-bottom: 1rem;
  display: block;
}

.feature-request-header h3 {
  font-size: 1.75rem;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 1rem;
}

.feature-request-header .feature-description {
  font-size: 1.1rem;
  color: #4a5568;
  line-height: 1.6;
  margin-bottom: 0;
}

@media (max-width: 768px) {
  .forms-feature-request {
    padding: 2rem 1rem;
    margin: 1rem;
  }
  
  .feature-request-header .feature-icon {
    font-size: 3rem;
  }
  
  .feature-request-header h3 {
    font-size: 1.5rem;
  }
  
  .feature-request-header .feature-description {
    font-size: 1rem;
  }
}

.delete-warning-banner {
  background: #c0392b;
  color: #fff;
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 12px;
  font-size: 0.95em;
  i {
    margin-right: 8px;
  }
}

.delete-reference-list {
  list-style: none;
  padding: 0;
  margin: 0 0 8px 0;
  li {
    padding: 8px 12px;
    border-bottom: 1px solid #eee;
    display: flex;
    align-items: center;
    gap: 8px;
  }
}

.ref-type-badge {
  background: #e74c3c;
  color: #fff;
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 0.75em;
  font-weight: 600;
  white-space: nowrap;
}

.ref-link {
  color: #2c3e50;
  text-decoration: underline;
  &:hover {
    color: #c0392b;
  }
  i {
    font-size: 0.8em;
    margin-left: 4px;
  }
}

.delete-acknowledge-label {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }
}

.shared-links-warning {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: #fff8e1;
  border: 1px solid #ffe082;
  border-radius: 6px;
  padding: 12px 14px;
  margin-top: 12px;
  i {
    color: #f59e0b;
    font-size: 1rem;
    margin-top: 2px;
    flex-shrink: 0;
  }
  strong {
    display: block;
    font-size: 0.875rem;
    color: #92400e;
    margin-bottom: 2px;
  }
  p {
    font-size: 0.8125rem;
    color: #78350f;
    margin: 0;
  }
}
</style>
<style lang="scss" scoped>
/* New-patient packet designation, in the packet modal.
   The whole label is the hit area — a bare 13px checkbox is a miserable target on a tablet, which
   is what the front desk actually uses. */
.np-packet-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
  cursor: pointer;
  font-weight: 600;

  input[type='checkbox'] {
    /* 16px keeps the box itself tappable; `margin: 0` stops Bootstrap's reboot nudging it out of
       line with the text. */
    width: 16px;
    height: 16px;
    margin: 0;
    cursor: pointer;
    flex-shrink: 0;
  }
}

.np-packet-hint {
  /* The app root is 14px, so 1rem would render at 14 — state the size in px and mean it. */
  font-size: 12px;
  line-height: 1.4;
}

/* Category + file name for the packet's merged PDF, indented under its checkbox so it reads as
   part of that setting. */
.packet-merge-fields {
  margin-top: 10px;
  padding-left: 24px;
}

.packet-merge-label {
  display: block;
  margin-bottom: 4px;
  font-size: 13px;
  font-weight: 600;
}

/* Get Link modal — two tabs: "Send to a patient" | "Website or QR code". */
.link-target-tabs {
  display: flex;
  border: 1px solid #0f7259;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 14px;
}

.link-target-tab {
  flex: 1 1 0;
  min-width: 0;
  padding: 9px 10px;
  border: none;
  background-color: #ffffff;
  color: #0f7259;
  font-weight: 600;
  font-size: 14px;
  line-height: 1.3;
  cursor: pointer;
}

.link-target-tab + .link-target-tab {
  border-left: 1px solid #0f7259;
}

.link-target-tab.active {
  background-color: #0f7259;
  color: #ffffff;
}

.link-target-tab:focus-visible {
  outline: 2px solid #0f7259;
  outline-offset: -4px;
}

.link-target-help {
  margin-bottom: 10px;
  font-size: 13px;
  color: #475569;
}

.link-target-subline {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 4px 12px;
  margin-top: 6px;
}

.link-target-hint {
  font-size: 12px;
  color: #64748b;
}

.link-refresh-patients,
.link-mode-switch-btn {
  padding: 0;
  font-size: 12px;
  color: #0f7259;
  vertical-align: baseline;
}

.link-mode-switch {
  margin-top: 10px;
  font-size: 12px;
  color: #475569;
}

.qr-for-caption {
  margin-top: 12px;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
}

/* Get Link modal — website tab: "Who will use this link?". */
.link-mode-group {
  margin-top: 16px;
  padding: 12px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background-color: #f8fafc;
}

.link-mode-title {
  font-weight: 600;
  margin-bottom: 8px;
}

.link-mode-option {
  font-weight: 600;
}

.link-mode-desc {
  display: block;
  font-size: 12px;
  line-height: 1.4;
  color: #64748b;
}

.link-mode-group ::v-deep .custom-control + .custom-control {
  margin-top: 6px;
}

.link-mode-note {
  margin-top: 8px;
  font-size: 12px;
  color: #64748b;
}
</style>
